/**
 * render/room.ts — Room Renderer
 *
 * The room is the world. Everything that happens in Hearth happens here,
 * inside these walls, on these tiles. The renderer's job is to make it feel
 * real — warm, alive, layered. Not photorealistic. Not flashy. Just home.
 *
 * Rendering order follows the painter's algorithm for isometric scenes:
 *   1. Background fill — mood-derived ambient light
 *   2. Floor tiles — the ground beneath everything
 *   3. Walls — the north and west edges that frame the room
 *   4. Entities — furniture and the player, sorted by isometric depth
 *   5. Special effects — Aeterna glow, lamp warmth, fern sway, fireplace flicker
 *   6. Mood overlay — subtle tint that colors the whole scene
 *   7. Dust motes — lazy particles drifting through lamplight
 *   8. Room vignette — soft edge darkening for depth
 *   9. Chat bubbles — speech floating above the player's actual head
 *  10. Furniture tooltip — warm label near the hovered tile
 *  11. UI chrome — time and room name
 *
 * "Does this feel like home?" — the question every frame must answer.
 */

import { GameState, asCamera, FurniturePlacement } from "../engine/state.js";
import { worldToScreen, applyCamera, TILE_W, TILE_H, TILE_W_HALF, TILE_H_HALF } from "../iso/projection.js";
import type { Camera, ScreenPoint } from "../iso/projection.js";
import { TileGrid, renderFloor, renderWalls, highlightTile } from "../iso/tile.js";
import type { WallSegment as TileWallSegment } from "../iso/tile.js";
import { depthSort, type Depthable } from "../iso/depth.js";
import {
  createCharacter, renderCharacter, updateAnimation,
  invalidateCharacter, setDeviceContext, setDeviceCaseContext,
  setAttentionContext, setWeaponContext, setStaggerContext,
  setEmoteContext, setBuffAuraContext,
  CHAR_W, CHAR_H,
  type CharacterState,
} from "../character/composer.js";
import { AnimationController } from "../character/animation.js";
import {
  createAttentionState, updateAttention, cursorToGaze, worldToGaze,
  type AttentionState,
} from "../character/attention.js";
import { renderFurniture, getFurnitureType, wallSnappedPosition, furnitureRenderPosition, getFootprintTiles, getVisualRotation } from "../room/furniture.js";
import { getMoodLighting, type MoodLighting } from "../room/mood.js";
import type { RoomMood } from "../room/mood.js";
import {
  toComposerDir, toComposerAnim, toComposerAppearance,
  toRegistryId, npcToComposerAppearance,
  getAppearanceVersion,
} from "./bridge.js";
import type { NpcState } from "../engine/state.js";
import { drawQuestGiverMark } from "./quest_ui.js";
import { getPresenceVisuals } from "../aeterna/presence.js";
import { HudRenderer } from "./hud.js";
import { renderCreature, type CreatureRenderContext } from "../creature/renderer.js";
import { getSpecies } from "../creature/species.js";
import { renderVehicle } from "../vehicle/renderer.js";
import { getWeaponVisual, getShieldVisual, getMomentumState } from "../combat/system.js";
import { findPath } from "../pathfinding/astar.js";

// Suppress unused-variable warnings for iso constants (kept for consumers)
void TILE_W; void TILE_H; void TILE_W_HALF; void TILE_H_HALF;

// ─── Constants ────────────────────────────────────────────────────────────────

/** Wall height in world units */
const WALL_HEIGHT = 3;

/** Mood lighting cache refresh interval */
const MOOD_CACHE_FRAMES = 60;

/** Font for the time display */
const TIME_FONT = "11px monospace";

/** Chat bubble font */
const BUBBLE_FONT = "bold 12px sans-serif";

/** Max characters before a bubble wraps */
const BUBBLE_MAX_CHARS = 28;

/** How many dust motes to maintain at high quality */
const DUST_MOTE_COUNT = 40;

/** Quadratic ease-out — fast start, slow finish */
const easeOut = (x: number): number => 1 - (1 - x) * (1 - x);
/** Quadratic ease-in — slow start, fast finish */
const easeIn = (x: number): number => x * x;

// ─── Tile Type Mapping ────────────────────────────────────────────────────────

const STATE_TILE_TO_ISO: Record<string, import("../iso/tile.js").TileType> = {
  wood:        "wood",
  stone:       "stone",
  carpet:      "carpet",
  grass:       "grass",
  marble:      "marble",
  dirt:        "dirt",
  water:       "water",
  cobblestone: "cobblestone",
  snow:        "snow",
  lava:        "lava",
  crystal:     "crystal",
  sand:        "sand",
  stone_dark:  "stone_dark",
  dock:        "dock",
};

// ─── Entity ───────────────────────────────────────────────────────────────────

interface RoomEntity extends Depthable {
  kind: "furniture" | "player" | "creature" | "vehicle" | "npc" | "dying_creature";
  screenX: number;
  screenY: number;
  furniture?: FurniturePlacement;
  creatureIdx?: number;   // index into state.creatures[]
  vehicleIdx?: number;    // index into state.vehicles[]
  npcIdx?: number;        // index into state.npcs[]
  dyingIdx?: number;      // index into state.dyingCreatures[]
}

// ─── Dust Mote ────────────────────────────────────────────────────────────────

interface DustMote {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;   // 0–1 (fades out toward 0)
  alpha: number;  // current draw alpha
  size: number;   // radius in pixels
  speed: number;  // max drift speed
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function bridgeMood(
  roomMood: { warmth: number; coziness: number; energy: number; mystery: number; nature: number; brightness: number; noise: number },
  gameHour: number
): RoomMood {
  const warmth = roomMood.warmth * 2 - 1;
  const energy = roomMood.energy * 2 - 1;
  const dayT   = Math.sin(((gameHour - 6) / 24) * Math.PI * 2);
  const brightness = Math.max(0, Math.min(1,
    roomMood.brightness * 0.6 + dayT * 0.3 + roomMood.warmth * 0.1
  ));

  return {
    warmth,
    coziness:   roomMood.coziness,
    energy,
    mystery:    roomMood.mystery,
    nature:     roomMood.nature,
    brightness,
    noise:      roomMood.noise,
  };
}

function formatGameTime(gameHour: number): string {
  const h          = ((gameHour % 24) + 24) % 24;
  const total      = Math.round(h * 60);
  const hour24     = Math.floor(total / 60) % 24;
  const minutes    = total % 60;
  const period     = hour24 < 12 ? "am" : "pm";
  const hour12     = hour24 % 12 || 12;
  return `${hour12}:${String(minutes).padStart(2, "0")} ${period}`;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.arcTo(x + w, y,     x + w, y + radius,     radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.arcTo(x + w, y + h, x + w - radius, y + h, radius);
  ctx.lineTo(x + radius, y + h);
  ctx.arcTo(x, y + h,    x, y + h - radius,       radius);
  ctx.lineTo(x, y + radius);
  ctx.arcTo(x, y,        x + radius, y,            radius);
  ctx.closePath();
}

function wrapText(text: string, maxChars: number): string[] {
  const words   = text.split(" ");
  const lines: string[] = [];
  let current = "";

  for (const word of words) {
    if (current.length === 0) {
      current = word;
    } else if (current.length + 1 + word.length <= maxChars) {
      current += " " + word;
    } else {
      lines.push(current);
      current = word;
    }
  }

  if (current.length > 0) lines.push(current);
  return lines.length > 0 ? lines : [""];
}

// ─── RoomRenderer ─────────────────────────────────────────────────────────────

export class RoomRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  // ── TileGrid cache ────────────────────────────────────────────────────────
  private tileGrid: TileGrid | null = null;
  private cachedRoomId: string | null = null;
  private cachedWalls: TileWallSegment[] = [];

  // ── Mood lighting cache ───────────────────────────────────────────────────
  private moodLighting: MoodLighting | null = null;
  private moodFrameCount = 0;

  // ── Character state ───────────────────────────────────────────────────────
  /** Lazily created on first render so OffscreenCanvas is available. */
  private charState: CharacterState | null = null;
  private animController: AnimationController = new AnimationController("idle", "s");
  /** Attention/gaze engine state — drives eye tracking, blink, hair dynamics */
  private attentionState: AttentionState = createAttentionState();
  /** Original heldItem before device swapped it — restored on device close */
  private savedHeldItem: number = -1;
  /** Track whether we've already swapped to avoid re-saving the device value */
  private deviceHeldItemActive = false;
  private lastTimestamp = 0;
  /** Cached appearance version — compared each frame to detect wardrobe changes */
  private cachedAppearanceVersion = -1;

  // ── NPC character states ─────────────────────────────────────────────────
  private npcCharStates = new Map<string, CharacterState>();
  /** Per-NPC attention engines — gives each NPC natural blink, gaze, and idle saccades */
  private npcAttentionStates = new Map<string, AttentionState>();

  // ── Offscreen entity canvas — composites living entities before mood tint ──
  // Drawing characters to a temporary canvas → source-atop tint → drawImage
  // ensures mood lighting only colors the entity pixels, never bleeding into
  // surrounding tiles or furniture. Also fixes creature hover + hit flash.
  private entityCanvas: OffscreenCanvas | null = null;
  private entityCtx: OffscreenCanvasRenderingContext2D | null = null;
  private static readonly ENTITY_CANVAS_W = 256; // generous for scaled characters
  private static readonly ENTITY_CANVAS_H = 384;
  // Anchor point in the entity canvas — entity's feet/origin maps here.
  // Leaves headroom above for character sprites and room below for shadows.
  private static readonly ENTITY_ANCHOR_X = 128;
  private static readonly ENTITY_ANCHOR_Y = 288;

  // ── Dying creature offscreen canvas (reused across frames) ────────────
  private deathCanvas: OffscreenCanvas | null = null;
  private deathCtx: OffscreenCanvasRenderingContext2D | null = null;
  private static readonly DEATH_CANVAS_SIZE = 128;

  // ── Dust motes ────────────────────────────────────────────────────────────
  private dustMotes: DustMote[] = [];

  // ── Click indicator particles ───────────────────────────────────────────
  private clickParticles: Array<{
    x: number; y: number; vx: number; vy: number;
    life: number; alpha: number; size: number;
  }> = [];

  // ── Path preview cache ────────────────────────────────────────────────
  private cachedPathPreview: Array<{ tileX: number; tileY: number }> | null = null;
  private cachedPathHoverTileX = -1;
  private cachedPathHoverTileY = -1;
  private lastPathComputeTime = 0;

  // ── Whisper overlay ─────────────────────────────────────────────────────
  private whisperText: string | null = null;
  private whisperAlpha = 0;
  private whisperTimer = 0;
  private whisperPhase: "fadein" | "hold" | "fadeout" | "idle" = "idle";
  private whisperQueue: string[] = [];
  private whisperDelay = 0;  // seconds before next whisper starts

  // ── Logical dimensions (updated each frame from state) ────────────────────
  /** Logical canvas width — use this instead of canvas.width which is the DPR-scaled buffer */
  private logW = 0;
  /** Logical canvas height */
  private logH = 0;

  // ── Mouse parallax ──────────────────────────────────────────────────────
  /** Set by the game loop each frame for subtle camera parallax */
  mouseX = 0;
  mouseY = 0;

  // ── Player screen position (canvas pixels, for attention calculations) ──
  private playerCanvasX = 0;
  private playerCanvasY = 0;
  private cameraZoom = 1;

  // ── Current frame state reference (set at render() entry) ─────────────────
  private _state: GameState | null = null;

  // ── HUD overlay ──────────────────────────────────────────────────────────
  readonly hud: HudRenderer;

  constructor(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) {
    this.canvas = canvas;
    this.ctx    = ctx;
    this.hud    = new HudRenderer(canvas, ctx);
  }

  /**
   * Process a raw canvas-space click against the HUD's hit zones.
   * Called in the UPDATE phase (before movement) from GameLoop.
   * Returns true if consumed — caller should set ui.uiClickConsumed.
   */
  handleUIClick(x: number, y: number, state: GameState): boolean {
    return this.hud.handleClick(x, y, state);
  }

  // ─── Main Entry Point ─────────────────────────────────────────────────────

  /**
   * Render one frame of the room scene.
   *
   * @param timestamp  DOMHighResTimeStamp from requestAnimationFrame
   * @param state      The full game state (read-only in practice)
   * @param quality    0.5–1.0 — effects below 0.7 are skipped for framerate
   */
  render(timestamp: number, state: GameState, quality: number): void {
    const { ctx } = this;
    const room = state.currentRoom;
    this.lastRoom = room ?? null;

    // Cache logical dimensions for private methods — ctx is DPR-scaled,
    // so we must use logical sizes, not canvas.width/height (buffer size)
    this.logW = state.canvasWidth;
    this.logH = state.canvasHeight;
    this._state = state;

    // Clear room-registered hit zones at frame start so toolbar buttons
    // registered during overlay rendering persist until the next frame's
    // handleClick can process them.
    this.hud.beginFrame();
    state.ui.hitRegions = [];

    if (!room) {
      ctx.fillStyle = "#0d0d14";
      ctx.fillRect(0, 0, this.logW, this.logH);
      return;
    }

    // Apply subtle mouse parallax — camera shifts slightly toward pointer
    const parallaxStrength = 4; // pixels max offset at screen edge
    const normMX = (this.mouseX / this.logW - 0.5) * 2; // -1..1
    const normMY = (this.mouseY / this.logH - 0.5) * 2;
    const rawCam = asCamera(state);

    // ── Screen shake — decaying camera offset on combat hits ──
    const shake = state.screenShake;
    let shakeOffX = 0, shakeOffY = 0;
    if (shake.intensity > 0.1) {
      // Use shake.phase (reset to 0 on each hit) so shake always starts at peak
      const t = shake.phase;
      shakeOffX = Math.sin(t * 43) * shake.intensity * (0.8 + Math.random() * 0.4);
      shakeOffY = Math.cos(t * 37) * shake.intensity * 0.7;
    }

    const camera: import("../iso/projection.js").Camera = {
      x: rawCam.x + normMX * parallaxStrength / rawCam.zoom + shakeOffX / rawCam.zoom,
      y: rawCam.y + normMY * parallaxStrength / rawCam.zoom + shakeOffY / rawCam.zoom,
      zoom: rawCam.zoom,
    };
    this.cameraZoom = camera.zoom;

    // dt in seconds; clamp to 100ms to survive tab-away spikes
    const dt = Math.min((timestamp - this.lastTimestamp) / 1000, 0.1);
    this.lastTimestamp = timestamp;

    // Screen shake decay is handled in loop.ts update() — NOT here in render().
    // Decaying in the renderer caused double-decay during scene transitions
    // where render() is called twice per tick.

    // ── Warm caches ───────────────────────────────────────────────────────
    this.ensureTileGrid(room);
    this.ensureMoodLighting(room, state);
    this.ensureCharacterState();

    const grid = this.tileGrid!;
    const mood = this.moodLighting!;

    // ════════════════════════════════════════════════════════════════════
    // 1. BACKGROUND
    // ════════════════════════════════════════════════════════════════════
    this.drawBackground(mood, state.time.gameHour);

    // ════════════════════════════════════════════════════════════════════
    // 2. FLOOR TILES
    // ════════════════════════════════════════════════════════════════════
    renderFloor(ctx, grid, camera, timestamp);

    // ════════════════════════════════════════════════════════════════════
    // 2b. FLOOR LIGHT POOLS + WINDOW SHAFTS
    // ════════════════════════════════════════════════════════════════════
    if (quality >= 0.7) {
      this.drawFloorLighting(timestamp, state, room, camera, mood);
    }

    // ════════════════════════════════════════════════════════════════════
    // 2c. WALL–FLOOR AMBIENT OCCLUSION
    // ════════════════════════════════════════════════════════════════════
    if (quality >= 0.7) {
      this.drawWallAO(room, camera, mood);
    }

    // ════════════════════════════════════════════════════════════════════
    // 3. WALLS
    // ════════════════════════════════════════════════════════════════════
    renderWalls(ctx, this.cachedWalls, camera, state.time.gameHour, timestamp);

    // ════════════════════════════════════════════════════════════════════
    // 3b. ENTITY SHADOWS
    // ════════════════════════════════════════════════════════════════════
    if (quality >= 0.7) {
      this.drawEntityShadows(state, room, camera, mood);
    }

    // ════════════════════════════════════════════════════════════════════
    // 3c. EDIT MODE GRID OVERLAY (subtle tile grid when in build mode)
    // ════════════════════════════════════════════════════════════════════
    if (state.ui.editMode) {
      this._editModeExitTime = null;
      this.drawEditModeGrid(state, room, camera, timestamp);
    } else if (this._editModeStartTime) {
      // Fade out — grid was just visible, now exiting
      if (!this._editModeExitTime) this._editModeExitTime = performance.now();
      const fadeOut = 1 - Math.min(1, (performance.now() - this._editModeExitTime) / 200);
      if (fadeOut > 0.01) {
        this.ctx.save();
        this.ctx.globalAlpha = fadeOut;
        this.drawEditModeGrid(state, room, camera, timestamp);
        this.ctx.restore();
      } else {
        this._editModeStartTime = null;
        this._editModeExitTime = null;
      }
    }

    // ════════════════════════════════════════════════════════════════════
    // 3d. TILE HOVER HIGHLIGHT + CLICK INDICATOR (on the floor, under entities)
    // ════════════════════════════════════════════════════════════════════
    this.drawTileHoverHighlight(timestamp, state, room, camera);
    this.drawClickIndicator(timestamp, state, room, camera);

    // ════════════════════════════════════════════════════════════════════
    // 4. DEPTH-SORTED ENTITIES (furniture + player)
    // ════════════════════════════════════════════════════════════════════
    this.drawEntities(timestamp, dt, state, room, camera, quality);

    // ════════════════════════════════════════════════════════════════════
    // 5. HOVER / SELECTION HIGHLIGHTS + PLACEMENT GHOST
    // ════════════════════════════════════════════════════════════════════
    this.drawFurnitureHighlights(timestamp, state, room, camera, grid);

    // ════════════════════════════════════════════════════════════════════
    // 5b. FOOTSTEP DUST PARTICLES
    // ════════════════════════════════════════════════════════════════════
    if (state.footstepParticles.length > 0) {
      this.drawFootstepParticles(state);
    }

    // ════════════════════════════════════════════════════════════════════
    // 5c. ARENA AMBIENT PARTICLES (sand wisps, torch sparks)
    // ════════════════════════════════════════════════════════════════════
    if (state.arenaParticles.length > 0) {
      this.drawArenaParticles(state);
    }

    // ════════════════════════════════════════════════════════════════════
    // 6. MOOD OVERLAY
    // ════════════════════════════════════════════════════════════════════
    if (quality >= 0.7) {
      this.drawMoodOverlay(mood);
    }

    // ════════════════════════════════════════════════════════════════════
    // 7. DUST MOTES
    // ════════════════════════════════════════════════════════════════════
    if (quality >= 0.7) {
      this.updateDustMotes(dt, room, camera);
      this.drawDustMotes(timestamp);
    }

    // ════════════════════════════════════════════════════════════════════
    // 8. ROOM VIGNETTE
    // ════════════════════════════════════════════════════════════════════
    this.drawVignette();

    // ════════════════════════════════════════════════════════════════════
    // 8b. COMBAT STANCE VIGNETTE (subtle red edge glow when weapon is drawn)
    // ════════════════════════════════════════════════════════════════════
    if (state.ui.combatStance || state.player.combatState.active) {
      this.drawCombatVignette(timestamp);
    }

    // ════════════════════════════════════════════════════════════════════
    // 9. CHAT BUBBLES
    // ════════════════════════════════════════════════════════════════════
    this.drawChatBubbles(state, camera);

    // ════════════════════════════════════════════════════════════════════
    // 10. FURNITURE TOOLTIP (hidden when toolbar or placement ghost visible)
    // ═══════════════════════════��═════════════════════════���══════════════
    if (state.ui.hoveredTile && !state.ui.selectedFurniture && !state.ui.placingFurnitureType) {
      this.drawFurnitureTooltip(state, room, camera);
    }

    // ════════════════════════════════════════════════════════════════════
    // 11. UI CHROME
    // ════════════════════════════════════════════════════════════════════
    this.drawTimeIndicator(state);

    // ════════════════════════════════════════════════════════════════════
    // 12. WHISPER OVERLAY
    // ════════════════════════════════════════════════════════════════════
    this.updateWhisper(dt);
    this.drawWhisper();

    // ════════════════════════════════════════════════════════════════════
    // 13. HUD OVERLAY
    // ════════════════════════════════════════════════════════════════════
    this.hud.draw(timestamp, state, this.logW, this.logH);

    // ════════════════════════════════════════════════════════════════════
    // 14. CURSOR
    // ════════════════════════════════════════════════════════════════════
    // Context-aware cursor: pointer for interactables, crosshair for placement,
    // not-allowed for blocked tiles, default for walkable ground.
    const hovered = state.ui.hoveredTile;
    const hoveredTile = hovered ? room.tiles[hovered.y]?.[hovered.x] : null;
    const creatureOnHover = hovered ? state.creatures.some(c => c.position.tileX === hovered.x && c.position.tileY === hovered.y) : false;
    const npcOnHover = hovered ? state.npcs.some(n => n.position.tileX === hovered.x && n.position.tileY === hovered.y) : false;
    const hudHover = this.hud.isMouseOverClickable();
    const cursorStyle =
      hudHover ? "pointer" :
      state.ui.deviceOpen ? "default" :
      state.ui.placingFurnitureType ? "crosshair" :
      state.ui.movingFurnitureId ? "crosshair" :
      (state.ui.editMode && state.ui.hoveredFurnitureId) ? "grab" :
      state.ui.hoveredFurnitureId ? "pointer" :
      (state.ui.combatStance && creatureOnHover) ? "crosshair" :
      creatureOnHover ? "crosshair" :
      npcOnHover ? "pointer" :
      (hoveredTile && !hoveredTile.walkable) ? "not-allowed" :
      "default";
    if (this.canvas.style.cursor !== cursorStyle) {
      this.canvas.style.cursor = cursorStyle;
    }

    // ── NPC state cleanup — prune departed NPCs every ~2 seconds ────────
    // npcCharStates and npcAttentionStates grow as NPCs are lazily created
    // but never shrink. Prune IDs that are no longer in state.npcs.
    if (this.moodFrameCount % 120 === 0 && this.npcCharStates.size > 0) {
      const liveIds = new Set(state.npcs.map(n => n.id));
      for (const id of this.npcCharStates.keys()) {
        if (!liveIds.has(id)) {
          this.npcCharStates.delete(id);
          this.npcAttentionStates.delete(id);
        }
      }
    }
  }

  // ─── Cache Management ─────────────────────────────────────────────────────

  private ensureTileGrid(room: import("../engine/state.js").Room): void {
    if (room.id === this.cachedRoomId && this.tileGrid !== null) return;

    const grid = new TileGrid(room.width, room.height);

    for (let ty = 0; ty < room.height; ty++) {
      const row = room.tiles[ty];
      if (!row) continue;
      for (let tx = 0; tx < room.width; tx++) {
        const cell = row[tx];
        if (!cell) continue;
        const isoType = STATE_TILE_TO_ISO[cell.type] ?? "floor";
        grid.setTile(tx, ty, isoType, cell.elevation);
      }
    }

    const tileWalls: TileWallSegment[] = room.walls.map((w) => ({
      x:         w.x,
      y:         w.y,
      side:      (w.side === "N" ? "n" : "w") as "n" | "w",
      height:    WALL_HEIGHT,
      hasWindow: w.hasWindow,
      hasDoor:   w.hasDoor,
      material:  (w.type ?? "plaster") as TileWallSegment["material"],
    }));

    this.tileGrid       = grid;
    this.cachedWalls    = tileWalls;
    this.cachedRoomId   = room.id;
    // Invalidate mood cache so it recomputes for the new room
    this.moodLighting   = null;
    this.moodFrameCount = 0;
    // Clear dust motes so they respawn relative to this room's lamps
    this.dustMotes      = [];
  }

  private ensureMoodLighting(
    room: import("../engine/state.js").Room,
    state: GameState
  ): void {
    this.moodFrameCount++;

    if (
      this.moodLighting !== null &&
      this.moodFrameCount % MOOD_CACHE_FRAMES !== 0
    ) {
      return;
    }

    const fullMood = bridgeMood(room.mood, state.time.gameHour);
    this.moodLighting = getMoodLighting(fullMood, state.time.gameHour);
  }

  /** Lazily create the composer CharacterState on first render. */
  private ensureCharacterState(): void {
    if (this.charState !== null) return;
    this.charState = createCharacter(toComposerAppearance());
  }

  // ─── Drawing Passes ───────────────────────────────────────────────────────

  private drawBackground(mood: MoodLighting, gameHour: number): void {
    const { ctx, logW, logH } = this;

    // Time-of-day sky gradient visible above walls (the "ceiling" area)
    const isNight = gameHour < 5.5 || gameHour > 20;
    const isDawn = gameHour >= 5.5 && gameHour < 7.5;
    const isDusk = gameHour >= 18 && gameHour <= 20;

    let skyTop: string, skyMid: string, skyBot: string;
    if (isNight) {
      skyTop = "#030308"; skyMid = "#080814"; skyBot = "#0e0e1c";
    } else if (isDawn) {
      const t = (gameHour - 5.5) / 2;
      skyTop = `rgb(${5 + t * 15|0},${5 + t * 10|0},${12 + t * 20|0})`;
      skyMid = `rgb(${12 + t * 30|0},${10 + t * 20|0},${20 + t * 25|0})`;
      skyBot = `rgb(${20 + t * 40|0},${15 + t * 25|0},${28 + t * 15|0})`;
    } else if (isDusk) {
      const t = (gameHour - 18) / 2;
      skyTop = `rgb(${20 - t * 15|0},${15 - t * 10|0},${32 - t * 20|0})`;
      skyMid = `rgb(${42 - t * 30|0},${30 - t * 20|0},${45 - t * 25|0})`;
      skyBot = `rgb(${60 - t * 40|0},${40 - t * 25|0},${43 - t * 15|0})`;
    } else {
      // Daytime — subtle blue-gray above
      skyTop = "#12121e"; skyMid = "#181828"; skyBot = "#1e1e30";
    }

    const skyGrad = ctx.createLinearGradient(0, 0, 0, logH);
    skyGrad.addColorStop(0, skyTop);
    skyGrad.addColorStop(0.35, skyMid);
    skyGrad.addColorStop(0.7, skyBot);
    skyGrad.addColorStop(1, "#0a0a12");
    ctx.fillStyle = skyGrad;
    ctx.fillRect(0, 0, logW, logH);

    // Mood ambient wash over the sky
    ctx.fillStyle = mood.ambient;
    ctx.fillRect(0, 0, logW, logH);

    // Dark fallback beneath everything
    ctx.globalCompositeOperation = "destination-over";
    ctx.fillStyle = "#0e0c14";
    ctx.fillRect(0, 0, logW, logH);
    ctx.globalCompositeOperation = "source-over";
  }

  /**
   * Draw warm light pools on the floor from lamps, fireplace, and windows.
   *
   * This is the pass that makes the room feel inhabited — warm circles of
   * light on the hardwood floor from a standing lamp, a golden fan of amber
   * from the fireplace, slanted parallelograms of daylight from windows.
   * All drawn with "screen" compositing so they brighten naturally.
   */
  private drawFloorLighting(
    timestamp: number,
    state: GameState,
    room: import("../engine/state.js").Room,
    camera: Camera,
    mood: MoodLighting
  ): void {
    const { ctx } = this;
    const zoom = camera.zoom;
    const hour = state.time.gameHour;

    ctx.save();
    ctx.globalCompositeOperation = "screen";

    // ── Lamp warm pools ───────────────────────────────────────────────
    for (const f of room.furniture) {
      const isLamp      = f.type === "floor_lamp" || f.type === "lamp";
      const isFireplace = f.type === "fireplace";

      if (isLamp && f.state["on"] !== false) {
        const snapped = furnitureRenderPosition(f, room.walls);
        const screen = applyCamera(
          worldToScreen(snapped.wx, snapped.wy, 0, true),
          camera
        );

        const poolR = 38 * zoom;
        const intensity = (f.state["intensity"] as number) ?? 0.8;

        const grad = ctx.createRadialGradient(
          screen.x, screen.y, 2 * zoom,
          screen.x, screen.y, poolR
        );
        grad.addColorStop(0, `rgba(255, 200, 100, ${(0.22 * intensity).toFixed(3)})`);
        grad.addColorStop(0.5, `rgba(255, 160, 60, ${(0.10 * intensity).toFixed(3)})`);
        grad.addColorStop(1, "rgba(200, 110, 30, 0)");

        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.ellipse(screen.x, screen.y, poolR, poolR * 0.5, 0, 0, Math.PI * 2);
        ctx.fill();
      }

      if (isFireplace && f.state["lit"] !== false) {
        const snapped = furnitureRenderPosition(f, room.walls);
        const screen = applyCamera(
          worldToScreen(snapped.wx, snapped.wy, 0, true),
          camera
        );

        // Animated fireplace glow — three overlapping sine waves for organic flicker
        const t = timestamp / 1000;
        const flicker1 = Math.sin(t * 3.7) * 0.08;
        const flicker2 = Math.sin(t * 7.1 + 1.3) * 0.05;
        const flicker3 = Math.sin(t * 11.3 + 2.7) * 0.03;
        const flickerMod = 1.0 + flicker1 + flicker2 + flicker3;

        // Primary fan glow
        const fanR = (55 + flicker1 * 30) * zoom;
        const baseAlpha = 0.22 * flickerMod;
        const grad = ctx.createRadialGradient(
          screen.x, screen.y - 8 * zoom, 4 * zoom,
          screen.x, screen.y + 10 * zoom, fanR
        );
        grad.addColorStop(0, `rgba(255, 140, 40, ${(baseAlpha * 1.1).toFixed(3)})`);
        grad.addColorStop(0.4, `rgba(255, 90, 20, ${(baseAlpha * 0.50).toFixed(3)})`);
        grad.addColorStop(1, "rgba(180, 50, 0, 0)");

        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.ellipse(screen.x, screen.y + 10 * zoom, fanR, fanR * 0.45, 0, 0, Math.PI * 2);
        ctx.fill();

        // Per-tile warm glow — individually illuminate tiles within 3-tile radius
        const fTileW = TILE_W * zoom;
        const fTileH = TILE_H * zoom;
        const halfTW = fTileW / 2;
        const halfTH = fTileH / 2;
        const glowRadius = 3;
        for (let dy = -1; dy <= glowRadius; dy++) {
          for (let dx = -glowRadius; dx <= glowRadius; dx++) {
            const gtx = f.tileX + dx;
            const gty = f.tileY + dy;
            if (gtx < 0 || gty < 0 || gtx >= room.width || gty >= room.height) continue;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist > glowRadius || dist < 0.5) continue;
            const falloff = 1 - dist / glowRadius;
            const tileAlpha = falloff * falloff * 0.08 * flickerMod;
            if (tileAlpha < 0.005) continue;
            const tileScreen = applyCamera(worldToScreen(gtx, gty, 0, true), camera);
            ctx.fillStyle = `rgba(255,140,40,${tileAlpha.toFixed(3)})`;
            ctx.beginPath();
            ctx.moveTo(tileScreen.x, tileScreen.y);
            ctx.lineTo(tileScreen.x + halfTW, tileScreen.y + halfTH);
            ctx.lineTo(tileScreen.x, tileScreen.y + fTileH);
            ctx.lineTo(tileScreen.x - halfTW, tileScreen.y + halfTH);
            ctx.closePath();
            ctx.fill();
          }
        }
      }
    }

    // ── Window light shafts (daytime only) ─────────────────────────────
    // Between 6am and 6pm, windows cast diagonal light onto the floor.
    // The shaft DIRECTION rotates with time of day:
    //   Morning (6am): sun in the east → light comes from right, shafts lean left
    //   Noon (12pm): sun overhead → short shafts straight down
    //   Evening (6pm): sun in the west → light comes from left, shafts lean right
    // Shaft LENGTH is longer at dawn/dusk (low sun angle), shorter at noon.
    const isDaytime = hour >= 5.5 && hour <= 18.5;
    if (isDaytime) {
      const noon = 12;
      const halfSpan = 6.5;
      const dayProgress = 1 - Math.abs(hour - noon) / halfSpan;
      const lightAlpha  = Math.max(0, dayProgress) * 0.12;

      // Sun angle: -1 at 6am (east), 0 at noon, +1 at 6pm (west)
      const sunAngle = (hour - noon) / halfSpan;
      // Shaft length: longer at dawn/dusk (low sun), shorter at noon (high sun)
      const baseLen  = 80 * zoom;
      const shaftLen = baseLen * (0.5 + 0.8 * Math.abs(sunAngle));
      const shaftW   = 20 * zoom;

      if (lightAlpha > 0.01) {
        for (const wall of room.walls) {
          if (!wall.hasWindow) continue;

          const windowScreen = applyCamera(
            worldToScreen(wall.x + 0.5, wall.y + 0.5, 1.5, true),
            camera
          );

          // Base direction depends on wall side, then modulated by sun angle
          // sunAngle shifts the X component: negative = light from east (morning),
          // positive = light from west (evening)
          let dx: number, dy: number;
          if (wall.side === "N") {
            // N windows face into the room (south). Sun angle shifts east-west.
            dx = shaftLen * (0.3 + sunAngle * 0.4);
            dy = shaftLen * 0.7;
          } else {
            // W windows face into the room (east). Sun angle shifts the lean.
            dx = shaftLen * 0.7;
            dy = shaftLen * (0.3 - sunAngle * 0.3);
          }

          const endX = windowScreen.x + dx;
          const endY = windowScreen.y + dy;
          const len  = Math.sqrt(dx * dx + dy * dy) || 1;

          // Color temperature shifts with time: warm orange at dawn/dusk, neutral at noon
          const warmth = Math.abs(sunAngle);
          const r = 255;
          const g = Math.round(240 - warmth * 40);
          const b = Math.round(200 - warmth * 80);

          const shaftGrad = ctx.createLinearGradient(
            windowScreen.x, windowScreen.y,
            endX, endY
          );
          shaftGrad.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${lightAlpha.toFixed(3)})`);
          shaftGrad.addColorStop(0.6, `rgba(${r}, ${g - 20}, ${b - 40}, ${(lightAlpha * 0.4).toFixed(3)})`);
          shaftGrad.addColorStop(1, `rgba(${r}, ${g - 40}, ${b - 80}, 0)`);

          ctx.fillStyle = shaftGrad;

          // Draw as a trapezoid — narrow at window, wide at far end
          const perpX = -dy / len;
          const perpY =  dx / len;

          ctx.beginPath();
          ctx.moveTo(windowScreen.x + perpX * shaftW * 0.3, windowScreen.y + perpY * shaftW * 0.3);
          ctx.lineTo(windowScreen.x - perpX * shaftW * 0.3, windowScreen.y - perpY * shaftW * 0.3);
          ctx.lineTo(endX - perpX * shaftW, endY - perpY * shaftW);
          ctx.lineTo(endX + perpX * shaftW, endY + perpY * shaftW);
          ctx.closePath();
          ctx.fill();
        }
      }
    }

    ctx.restore();
  }

  /**
   * Draw soft ambient occlusion at the base of every wall.
   *
   * Where vertical surfaces meet horizontal ones, light scatters less —
   * the corner is darker. This gradient along each wall's floor edge
   * anchors the wall to the floor and creates a sense of weight.
   */
  private drawWallAO(
    room: import("../engine/state.js").Room,
    camera: Camera,
    mood: MoodLighting
  ): void {
    const { ctx } = this;
    const zoom  = camera.zoom;
    const halfW = (TILE_W * zoom) / 2;
    const halfH = (TILE_H * zoom) / 2;
    const aoSpread = 8 * zoom;  // how far the shadow extends from the wall base

    ctx.save();
    ctx.globalCompositeOperation = "multiply";

    for (const wall of room.walls) {
      const raw    = worldToScreen(wall.x, wall.y, 0, true);
      const screen = applyCamera(raw, camera);

      // Wall base edge in screen space (same geometry as tile.ts drawWallSegment)
      let bx0: number, by0: number, bx1: number, by1: number;
      if (wall.side === "N") {
        bx0 = screen.x - halfW; by0 = screen.y + halfH;
        bx1 = screen.x;         by1 = screen.y;
      } else {
        bx0 = screen.x;         by0 = screen.y;
        bx1 = screen.x + halfW; by1 = screen.y + halfH;
      }

      // AO gradient perpendicular to the wall base, fading into the floor
      // For N walls: fade toward +y (into the room)
      // For W walls: fade toward +x (into the room)
      const mx = (bx0 + bx1) / 2;
      const my = (by0 + by1) / 2;
      const dx = wall.side === "N" ? halfW * 0.5 : halfW * 0.5;
      const dy = wall.side === "N" ? halfH + aoSpread : halfH + aoSpread;

      const aoGrad = ctx.createLinearGradient(mx, my, mx + dx * 0.3, my + dy * 0.3);
      const shadowStr = mood.shadowAlpha * 0.35;
      aoGrad.addColorStop(0, `rgba(10, 8, 16, ${shadowStr.toFixed(3)})`);
      aoGrad.addColorStop(1, "rgba(10, 8, 16, 0)");

      ctx.fillStyle = aoGrad;
      ctx.beginPath();
      ctx.moveTo(bx0, by0);
      ctx.lineTo(bx1, by1);
      ctx.lineTo(bx1 + dx * 0.4, by1 + dy * 0.4);
      ctx.lineTo(bx0 + dx * 0.4, by0 + dy * 0.4);
      ctx.closePath();
      ctx.fill();
    }

    // ── Room corner darkening ─────────────────────────────────────────
    // Where an N wall ends and a W wall begins (upper-left corner of the
    // room), there's a deeper shadow. We detect this by looking for N walls
    // at x where there's also a W wall at x+1 with the same y (or vice versa).
    const wallSet = new Set(room.walls.map(w => `${w.side}:${w.x}:${w.y}`));
    for (const wall of room.walls) {
      if (wall.side !== "N") continue;
      // Check if there's a W wall one tile to the right that shares the corner
      if (wallSet.has(`W:${wall.x}:${wall.y}`)) {
        // This tile has both an N and W wall meeting — it's a corner
        const cornerScreen = applyCamera(worldToScreen(wall.x, wall.y, 0, true), camera);

        const cornerR = 12 * zoom;
        const cornerAlpha = mood.shadowAlpha * 0.25;
        const cornerGrad = ctx.createRadialGradient(
          cornerScreen.x, cornerScreen.y, 1 * zoom,
          cornerScreen.x, cornerScreen.y, cornerR
        );
        cornerGrad.addColorStop(0, `rgba(6, 4, 12, ${cornerAlpha.toFixed(3)})`);
        cornerGrad.addColorStop(1, "rgba(6, 4, 12, 0)");
        ctx.fillStyle = cornerGrad;
        ctx.beginPath();
        ctx.arc(cornerScreen.x, cornerScreen.y, cornerR, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.restore();
  }

  /**
   * Draw isometric ellipse shadows beneath every entity.
   *
   * Shadows are drawn in a pre-pass before the entities themselves so they
   * appear on the floor beneath everything. The shadow opacity is modulated
   * by the mood's shadowAlpha — bright daylight casts strong shadows,
   * candlelit evenings cast almost none.
   */
  private drawEntityShadows(
    state: GameState,
    room: import("../engine/state.js").Room,
    camera: Camera,
    mood: MoodLighting
  ): void {
    const { ctx } = this;
    const sa = mood.shadowAlpha;
    if (sa < 0.02) return;  // skip in near-darkness

    ctx.save();
    ctx.globalCompositeOperation = "multiply";

    // ── Furniture shadows ──────────────────────────────────────────────
    for (const placement of room.furniture) {
      if (placement.id === state.ui.movingFurnitureId) continue;
      if (placement.elevation > 0) continue;  // elevated items (on desk) don't cast floor shadows
      const registryId = toRegistryId(placement.type);
      const ft = getFurnitureType(registryId);
      if (!ft) continue;
      if (ft.isFloor) continue;  // no shadow for rugs
      if (registryId === "aeterna") continue;  // Aeterna has its own glow

      const { wx, wy } = furnitureRenderPosition(placement, room.walls);
      const screen = applyCamera(worldToScreen(wx, wy, 0, true), camera);

      // Scale shadow to furniture size — taller/wider sprites get bigger shadows
      const spriteW = ft.sprite?.width ?? 40;
      const spriteH = ft.sprite?.height ?? 48;
      const shadowW = Math.max(8, spriteW * 0.38) * camera.zoom;
      const shadowH = Math.max(4, spriteH * 0.12) * camera.zoom;
      const shadowOffX = 2 * camera.zoom;
      const shadowOffY = 2 * camera.zoom;

      ctx.fillStyle = `rgba(10, 8, 16, ${(sa * 0.4).toFixed(3)})`;
      ctx.beginPath();
      ctx.ellipse(
        screen.x + shadowOffX, screen.y + shadowOffY,
        shadowW, shadowH,
        0, 0, Math.PI * 2
      );
      ctx.fill();
    }

    // ── Player shadow ──────────────────────────────────────────────────
    const p = state.player;
    let playerTileX = p.position.tileX;
    let playerTileY = p.position.tileY;
    const shadowNext = p.movementQueue?.[0];
    if (p.moveProgress > 0 && shadowNext) {
      playerTileX += (shadowNext.tileX - p.position.tileX) * p.moveProgress;
      playerTileY += (shadowNext.tileY - p.position.tileY) * p.moveProgress;
    }
    const playerScreen = applyCamera(
      worldToScreen(playerTileX + 0.5, playerTileY + 0.5, p.position.elevation),
      camera
    );

    const pShadowW = 12 * camera.zoom;
    const pShadowH = 5 * camera.zoom;
    ctx.fillStyle = `rgba(10, 8, 16, ${(sa * 0.45).toFixed(3)})`;
    ctx.beginPath();
    ctx.ellipse(
      playerScreen.x + 2 * camera.zoom, playerScreen.y + 2 * camera.zoom,
      pShadowW, pShadowH,
      0, 0, Math.PI * 2
    );
    ctx.fill();

    // ── Creature shadows ──────────────────────────────────────────────
    for (const creature of state.creatures) {
      let cx = creature.position.tileX;
      let cy = creature.position.tileY;
      if (creature.moveProgress > 0 && creature.movementQueue.length > 0) {
        const target = creature.movementQueue[0];
        cx += (target.tileX - cx) * creature.moveProgress;
        cy += (target.tileY - cy) * creature.moveProgress;
      }
      const cScreen = applyCamera(
        worldToScreen(cx + 0.5, cy + 0.5, creature.position.elevation),
        camera
      );
      const cShadowW = 8 * camera.zoom;
      const cShadowH = 3.5 * camera.zoom;
      ctx.fillStyle = `rgba(10, 8, 16, ${(sa * 0.3).toFixed(3)})`;
      ctx.beginPath();
      ctx.ellipse(
        cScreen.x + 1 * camera.zoom, cScreen.y + 1 * camera.zoom,
        cShadowW, cShadowH,
        0, 0, Math.PI * 2
      );
      ctx.fill();
    }

    ctx.restore();
  }

  /**
   * Build the entity list, depth-sort it, and draw each entity.
   *
   * Every piece of furniture and the player are unified into a single
   * sortable list. The depth formula ensures chairs appear in front of
   * tables, and people appear in front of furniture they stand beside.
   */
  private drawEntities(
    timestamp: number,
    dt: number,
    state: GameState,
    room: import("../engine/state.js").Room,
    camera: Camera,
    quality: number = 1.0
  ): void {
    const entities: RoomEntity[] = [];

    // ── Furniture ─────────────────────────────────────────────────────────
    // First pass: compute RENDER positions for ground-level furniture.
    // Uses furnitureRenderPosition which anchors multi-tile items at the
    // south corner of the full footprint (not the first tile center).
    // Elevated items inherit their parent's position.
    const furniturePositions = new Map<string, { wx: number; wy: number }>();
    for (const placement of room.furniture) {
      if (placement.id === state.ui.movingFurnitureId) continue;
      if (placement.elevation > 0) continue; // defer elevated items
      furniturePositions.set(placement.id, furnitureRenderPosition(placement, room.walls));
    }
    for (const placement of room.furniture) {
      if (placement.id === state.ui.movingFurnitureId) continue;
      if (placement.elevation <= 0) continue;
      // Find ground-level furniture at the same tile to inherit snap from
      let parentSnap: { wx: number; wy: number } | undefined;
      for (const other of room.furniture) {
        if (other.elevation === 0 && other.tileX === placement.tileX && other.tileY === placement.tileY) {
          parentSnap = furniturePositions.get(other.id);
          break;
        }
      }
      furniturePositions.set(placement.id, parentSnap ?? furnitureRenderPosition(placement, room.walls));
    }

    for (const placement of room.furniture) {
      // Skip the piece being repositioned — it's shown as a placement ghost instead
      if (placement.id === state.ui.movingFurnitureId) continue;
      const pos = furniturePositions.get(placement.id);
      const wx = pos?.wx ?? placement.tileX + 0.5;
      const wy = pos?.wy ?? placement.tileY + 0.5;
      const wz = placement.elevation;

      const screen = applyCamera(worldToScreen(wx, wy, wz, true), camera);

      // Flat floor items (rugs, mats) are drawn in a pre-pass before the
      // depth-sorted entity loop so they always render beneath standing
      // characters regardless of tile overlap.
      const ft = getFurnitureType(toRegistryId(placement.type));
      if (ft?.isFloor) {
        const { ctx } = this;
        ctx.save();
        ctx.translate(screen.x, screen.y);
        ctx.scale(camera.zoom, camera.zoom);
        this.drawFurnitureEntity(timestamp, placement, 0, 0);
        this.drawFurnitureEffects(timestamp, placement, 0, 0);
        ctx.restore();
        continue;
      }

      entities.push({
        kind:      "furniture",
        wx, wy, wz,
        height:    1,
        screenX:   screen.x,
        screenY:   screen.y,
        furniture: placement,
      });
    }

    // ── Player ────────────────────────────────────────────────────────────
    const p = state.player;
    let playerTileX = p.position.tileX;
    let playerTileY = p.position.tileY;

    const entityNext = p.movementQueue?.[0];
    if (p.moveProgress > 0 && entityNext) {
      playerTileX += (entityNext.tileX - p.position.tileX) * p.moveProgress;
      playerTileY += (entityNext.tileY - p.position.tileY) * p.moveProgress;
    }

    let playerWx = playerTileX + 0.5;
    let playerWy = playerTileY + 0.5;
    const playerWz = p.position.elevation;

    // When sitting/sleeping on furniture, align with the furniture's wall-snap
    // so the character renders on the piece, not at raw tile center.
    if ((p.animState === "sit" || p.animState === "sleep" || p.animState === "device_sit") && room.walls.length > 0) {
      const seated = room.furniture.find(
        f => f.tileX === p.position.tileX && f.tileY === p.position.tileY && f.elevation === 0
      );
      if (seated) {
        const snapped = wallSnappedPosition(seated, room.walls);
        playerWx = snapped.wx;
        playerWy = snapped.wy;
      }
    }

    const playerScreen = applyCamera(worldToScreen(playerWx, playerWy, playerWz), camera);

    // When sitting/sleeping, use a reduced height so the depth tiebreaker
    // places the player IN FRONT of (drawn after) the furniture they're on.
    // Standing height 1.5 → tiebreaker 3 (sorts behind furniture tiebreaker 5).
    // Sitting height 0.5 → tiebreaker 7 (sorts in front of furniture).
    const isSitting = p.animState === "sit" || p.animState === "sleep" || p.animState === "device_sit";
    const playerHeight = isSitting ? 0.5 : 1.5;

    entities.push({
      kind:    "player",
      wx:      playerWx,
      wy:      playerWy,
      wz:      playerWz,
      height:  playerHeight,
      screenX: playerScreen.x,
      screenY: playerScreen.y,
    });

    // Store player screen position for device animation origin
    state.ui.playerScreenX = playerScreen.x;
    state.ui.playerScreenY = playerScreen.y;

    // ── Creatures ──────────────────────────────────────────────────────────
    for (let i = 0; i < state.creatures.length; i++) {
      const creature = state.creatures[i];
      // Skip mounted creatures — they render as part of the rider, not standalone
      if (creature.mountState?.mounted) continue;
      let cx = creature.position.tileX;
      let cy = creature.position.tileY;

      // Interpolate position during movement
      if (creature.moveProgress > 0 && creature.movementQueue.length > 0) {
        const target = creature.movementQueue[0];
        cx += (target.tileX - cx) * creature.moveProgress;
        cy += (target.tileY - cy) * creature.moveProgress;
      }

      const cwx = cx + 0.5;
      const cwy = cy + 0.5;
      const cwz = creature.position.elevation;

      const cScreen = applyCamera(worldToScreen(cwx, cwy, cwz), camera);

      entities.push({
        kind: "creature",
        wx: cwx,
        wy: cwy,
        wz: cwz,
        height: 0.8,
        screenX: cScreen.x,
        screenY: cScreen.y,
        creatureIdx: i,
      });
    }

    // ── Vehicles ───────────────────────────────────────────────────────────
    for (let i = 0; i < state.vehicles.length; i++) {
      const vehicle = state.vehicles[i];
      const vwx = vehicle.position.tileX + 0.5;
      const vwy = vehicle.position.tileY + 0.5;
      const vwz = vehicle.position.elevation;

      const vScreen = applyCamera(worldToScreen(vwx, vwy, vwz), camera);

      entities.push({
        kind: "vehicle",
        wx: vwx,
        wy: vwy,
        wz: vwz,
        height: 1.2,
        screenX: vScreen.x,
        screenY: vScreen.y,
        vehicleIdx: i,
      });
    }

    // ── NPCs ──────────────────────────────────────────────────────────────
    for (let i = 0; i < state.npcs.length; i++) {
      const npcState = state.npcs[i];
      const nwx = npcState.position.tileX + 0.5;
      const nwy = npcState.position.tileY + 0.5;
      const nwz = npcState.position.elevation;

      const nScreen = applyCamera(worldToScreen(nwx, nwy, nwz), camera);

      entities.push({
        kind: "npc",
        wx: nwx,
        wy: nwy,
        wz: nwz,
        height: 1.5,
        screenX: nScreen.x,
        screenY: nScreen.y,
        npcIdx: i,
      });
    }

    // ── Dying creatures (participate in depth sort, not always on top) ──
    for (let i = 0; i < state.dyingCreatures.length; i++) {
      const dc = state.dyingCreatures[i];
      const cwx = dc.creature.position.tileX + 0.5;
      const cwy = dc.creature.position.tileY + 0.5;
      const cwz = dc.creature.position.elevation;
      const cScreen = applyCamera(worldToScreen(cwx, cwy, cwz), camera);
      entities.push({
        kind: "dying_creature",
        wx: cwx,
        wy: cwy,
        wz: cwz,
        height: 0.8,
        screenX: cScreen.x,
        screenY: cScreen.y,
        dyingIdx: i,
      });
    }

    depthSort(entities);

    // ── Draw in depth order ───────────────────────────────────────────────
    // Tiles already scale via tileW/tileH * zoom, but entity sprites are
    // drawn at fixed pixel sizes. We translate to each entity's screen
    // anchor, scale by camera zoom, then draw at the local origin so
    // characters and furniture resize with the viewport.
    for (const entity of entities) {
      const { ctx } = this;
      ctx.save();
      ctx.translate(entity.screenX, entity.screenY);
      ctx.scale(camera.zoom, camera.zoom);

      // In edit mode, fade characters and vehicles so furniture is the focus
      const isCharacter = entity.kind === "player" || entity.kind === "creature" || entity.kind === "npc";
      const isVehicle = entity.kind === "vehicle";
      if ((isCharacter || isVehicle) && state.ui.editMode) {
        ctx.globalAlpha = 0.35;
      }

      if (entity.kind === "furniture" && entity.furniture) {
        this.drawFurnitureEntity(timestamp, entity.furniture, 0, 0);
        this.drawFurnitureEffects(timestamp, entity.furniture, 0, 0);
        // Register isometric diamond hit regions per footprint tile
        const footTiles = getFootprintTiles(entity.furniture);
        const hw = TILE_W_HALF * camera.zoom;
        const hh = TILE_H_HALF * camera.zoom;
        const diamonds: Array<{ cx: number; cy: number; hw: number; hh: number }> = [];
        for (const ft of footTiles) {
          const tc = applyCamera(worldToScreen(ft.x + 0.5, ft.y + 0.5, 0, true), camera);
          diamonds.push({ cx: tc.x, cy: tc.y, hw, hh });
        }
        state.ui.hitRegions.push({
          id: entity.furniture.id,
          priority: 100,
          x: 0, y: 0, w: 0, h: 0, // unused — diamond test takes precedence
          diamonds,
        });
      } else if (entity.kind === "creature" && entity.creatureIdx != null) {
        const creature = state.creatures[entity.creatureIdx];
        if (creature) {
          const species = getSpecies(creature.speciesId);
          if (species) {
            const facing = creature.direction === "W" || creature.direction === "NW" || creature.direction === "N" || creature.direction === "SW" ? -1 : 1;
            const roomMood = room.mood;
            // Gather nearby creature positions for social gaze
            const nearby: Array<{ tileX: number; tileY: number }> = [];
            for (const other of state.creatures) {
              if (other.id === creature.id) continue;
              if (other.mountState?.mounted) continue; // mounted creature positions are stale
              const dx = other.position.tileX - creature.position.tileX;
              const dy = other.position.tileY - creature.position.tileY;
              if (dx * dx + dy * dy < 25) { // within 5 tiles
                nearby.push(other.position);
              }
            }

            // ── Offscreen compositing — draw creature to entity canvas ──
            const eCtx = this.ensureEntityCanvas();
            const ECW = RoomRenderer.ENTITY_CANVAS_W;
            const ECH = RoomRenderer.ENTITY_CANVAS_H;
            const eAX = RoomRenderer.ENTITY_ANCHOR_X;
            const eAY = RoomRenderer.ENTITY_ANCHOR_Y;
            eCtx.clearRect(0, 0, ECW, ECH);
            eCtx.save();
            eCtx.translate(eAX, eAY);

            const rc: CreatureRenderContext = {
              ctx: eCtx as unknown as CanvasRenderingContext2D,
              species,
              pose: creature.pose,
              rootPos: { x: 0, y: 0 },
              scale: 1.0,
              facing,
              time: timestamp / 1000,
              quality,
              isWalking: creature.behaviorState.current === "wander" || creature.behaviorState.current === "follow" || creature.behaviorState.current === "patrol",
              ambientGlow: roomMood.warmth > 0.6 ? `rgba(255, 160, 60, ${(roomMood.warmth - 0.6) * 0.3})` : undefined,
              playerTileX: state.player.position.tileX,
              playerTileY: state.player.position.tileY,
              creatureTileX: creature.position.tileX,
              creatureTileY: creature.position.tileY,
              creatureId: creature.id,
              nearbyCreatures: nearby.length > 0 ? nearby : undefined,
            };
            try { renderCreature(rc); } catch { /* isolate bad creature state from crashing the frame */ }

            // ── Creature combat hover — red pulsing outline when targeting ──
            // source-atop is now safe: it only touches creature pixels on the
            // isolated entity canvas, never bleeding into tiles or furniture.
            const isHoveredCreature = state.ui.hoveredTile
              && creature.position.tileX === state.ui.hoveredTile.x
              && creature.position.tileY === state.ui.hoveredTile.y
              && (state.ui.combatStance || state.player.combatState.active);
            if (isHoveredCreature) {
              const hoverPulse = 0.35 + Math.sin(timestamp * 0.006) * 0.15;
              eCtx.globalCompositeOperation = "source-atop";
              eCtx.globalAlpha = hoverPulse;
              eCtx.fillStyle = "rgba(255, 60, 40, 0.6)";
              eCtx.fillRect(-eAX, -eAY, ECW, ECH);
              eCtx.globalCompositeOperation = "source-over";
              eCtx.globalAlpha = 1;
            }

            // ── Creature hit flash — brief white overlay on damage ──
            const hitTime = state.creatureHitTimes.get(creature.id);
            if (hitTime) {
              const hitAge = (timestamp / 1000) - hitTime;
              if (hitAge < 0.15) {
                const flashAlpha = (1 - hitAge / 0.15) * 0.45;
                eCtx.globalCompositeOperation = "source-atop";
                eCtx.globalAlpha = flashAlpha;
                eCtx.fillStyle = "#FFFFFF";
                eCtx.fillRect(-eAX, -eAY, ECW, ECH);
                eCtx.globalCompositeOperation = "source-over";
                eCtx.globalAlpha = 1;
              }
            }

            // ── Mood lighting tint — ambient room color on creature pixels ──
            if (this.moodLighting) {
              const tintColor = this.getEntityLightTint(this.moodLighting, state.time.gameHour, entity.wx, entity.wy, room);
              if (tintColor) {
                eCtx.globalCompositeOperation = "source-atop";
                eCtx.fillStyle = tintColor;
                eCtx.fillRect(-eAX, -eAY, ECW, ECH);
                eCtx.globalCompositeOperation = "source-over";
              }
            }

            eCtx.restore();

            // Composite entity canvas onto main canvas — zoom is already applied
            ctx.drawImage(this.entityCanvas!, -eAX, -eAY);

            // ── Outer targeting glow — drawn on main canvas (bleeds intentionally) ──
            if (isHoveredCreature) {
              const hoverPulse = 0.35 + Math.sin(timestamp * 0.006) * 0.15;
              ctx.shadowColor = `rgba(255, 60, 40, ${hoverPulse})`;
              ctx.shadowBlur = 10;
              ctx.strokeStyle = `rgba(255, 80, 60, ${hoverPulse + 0.1})`;
              ctx.lineWidth = 1.5;
              ctx.strokeRect(-55, -95, 110, 170);
              ctx.shadowBlur = 0;
            }
          }
        }
      } else if (entity.kind === "vehicle" && entity.vehicleIdx != null) {
        const vehicle = state.vehicles[entity.vehicleIdx];
        if (vehicle) {
          const vFacing = vehicle.direction === "W" || vehicle.direction === "NW" || vehicle.direction === "N" || vehicle.direction === "SW" ? -1 : 1;
          renderVehicle(ctx, vehicle, 0, 0, 1.0, vFacing);
        }
      } else if (entity.kind === "npc" && entity.npcIdx != null) {
        const npcData = state.npcs[entity.npcIdx];
        if (npcData) {
          // ── Offscreen compositing — draw NPC to entity canvas ──
          const eCtx = this.ensureEntityCanvas();
          const ECW = RoomRenderer.ENTITY_CANVAS_W;
          const ECH = RoomRenderer.ENTITY_CANVAS_H;
          const eAX = RoomRenderer.ENTITY_ANCHOR_X;
          const eAY = RoomRenderer.ENTITY_ANCHOR_Y;
          eCtx.clearRect(0, 0, ECW, ECH);
          eCtx.save();
          eCtx.translate(eAX, eAY);
          eCtx.scale(0.5, 0.5);

          // Temporarily swap ctx so drawNpcEntity + renderCharacter write to entity canvas
          const mainCtxRef = this.ctx;
          (this as any).ctx = eCtx as unknown as CanvasRenderingContext2D;
          this.drawNpcEntity(dt, state, npcData, 0, 0);
          (this as any).ctx = mainCtxRef;

          eCtx.restore();

          // ── Mood lighting tint on NPC ──
          if (this.moodLighting) {
            const tintColor = this.getEntityLightTint(this.moodLighting, state.time.gameHour, entity.wx, entity.wy, room);
            if (tintColor) {
              eCtx.globalCompositeOperation = "source-atop";
              eCtx.fillStyle = tintColor;
              eCtx.fillRect(0, 0, ECW, ECH);
              eCtx.globalCompositeOperation = "source-over";
            }
          }

          // Composite entity canvas onto main canvas
          ctx.drawImage(this.entityCanvas!, -eAX, -eAY);
        }
      } else if (entity.kind === "player") {
        // Store actual canvas-pixel position for cursor attention tracking
        this.playerCanvasX = entity.screenX;
        this.playerCanvasY = entity.screenY;

        // ── Offscreen compositing — draw player to entity canvas ──
        const eCtx = this.ensureEntityCanvas();
        const ECW = RoomRenderer.ENTITY_CANVAS_W;
        const ECH = RoomRenderer.ENTITY_CANVAS_H;
        const eAX = RoomRenderer.ENTITY_ANCHOR_X;
        const eAY = RoomRenderer.ENTITY_ANCHOR_Y;
        eCtx.clearRect(0, 0, ECW, ECH);
        eCtx.save();
        eCtx.translate(eAX, eAY);
        // Character sprites are 96×144 (2× internal resolution) — scale to match furniture
        eCtx.scale(0.5, 0.5);

        // Temporarily swap ctx so drawPlayerEntity + renderCharacter write to entity canvas
        const mainCtxRef = this.ctx;
        (this as any).ctx = eCtx as unknown as CanvasRenderingContext2D;
        this.drawPlayerEntity(dt, state, 0, 0);
        (this as any).ctx = mainCtxRef;

        eCtx.restore();

        // ── Mood lighting tint on player ──
        if (this.moodLighting) {
          const tintColor = this.getEntityLightTint(this.moodLighting, state.time.gameHour, entity.wx, entity.wy, room);
          if (tintColor) {
            eCtx.globalCompositeOperation = "source-atop";
            eCtx.fillStyle = tintColor;
            eCtx.fillRect(0, 0, ECW, ECH);
            eCtx.globalCompositeOperation = "source-over";
          }
        }

        // Ghost mode — player becomes translucent when dead
        const ghosting = state.player.death.ghostMode;
        if (ghosting) ctx.globalAlpha = state.player.death.ghostAlpha;

        // Composite entity canvas onto main canvas — zoom is already applied
        ctx.drawImage(this.entityCanvas!, -eAX, -eAY);

        if (ghosting) ctx.globalAlpha = 1;
      } else if (entity.kind === "dying_creature" && entity.dyingIdx != null) {
        // Undo entity loop's translate/scale — dying creature manages its own
        ctx.restore();
        this.drawDyingCreatureEntity(timestamp, state, entity.dyingIdx, entity.screenX, entity.screenY, camera.zoom, quality);
        ctx.save(); // balance the restore() at end of loop iteration
      }

      ctx.restore();
    }

  }

  /**
   * Draw one furniture piece using the real furniture renderer.
   *
   * Falls back to a warm neutral block for unregistered types so nothing
   * disappears silently from the room.
   */

  /** Lazily create the shared offscreen canvas used for entity mood tinting */
  private ensureEntityCanvas(): OffscreenCanvasRenderingContext2D {
    if (!this.entityCanvas) {
      this.entityCanvas = new OffscreenCanvas(
        RoomRenderer.ENTITY_CANVAS_W, RoomRenderer.ENTITY_CANVAS_H
      );
      this.entityCtx = this.entityCanvas.getContext("2d");
    }
    return this.entityCtx!;
  }

  // ─── Dying Creature Rendering ─────────────────────────────────────────────
  //
  // Multi-phase death animation rendered via offscreen canvas to isolate
  // internal globalAlpha overrides from the death fade alpha.
  // Now called from the depth-sorted entity loop so dying creatures correctly
  // render behind/in-front-of furniture and other entities.

  private drawDyingCreatureEntity(
    timestamp: number,
    state: GameState,
    dyingIdx: number,
    screenX: number,
    screenY: number,
    zoom: number,
    quality: number,
  ): void {
    const dc = state.dyingCreatures[dyingIdx];
    if (!dc) return;

    const nowSec = timestamp / 1000;
    const mainCtx = this.ctx;

    const t = Math.min(1, (nowSec - dc.startTime) / dc.duration);
    const creature = dc.creature;
    const species = getSpecies(creature.speciesId);
    if (!species) return;

    // Size the offscreen canvas to fit this species
    const neededSize = Math.max(
      RoomRenderer.DEATH_CANVAS_SIZE,
      Math.ceil((species.bodyShape.bodyLength + (species.bodyShape.neckLength || 0) * 5 + species.bodyShape.headRadius + (species.bodyShape.wingSpan || 0) * 0.5 + species.bodyShape.legLength * 4) * 2 + 32),
    );
    if (!this.deathCanvas || this.deathCanvas.width < neededSize) {
      this.deathCanvas = new OffscreenCanvas(neededSize, neededSize);
      this.deathCtx = this.deathCanvas.getContext("2d");
    }
    const offCtx = this.deathCtx;
    if (!offCtx) return;
    const ds = this.deathCanvas.width;
    const halfDS = ds / 2;

    const facing = creature.direction === "W" || creature.direction === "NW" || creature.direction === "N" || creature.direction === "SW" ? -1 : 1;

    // ── Phase calculations ──
    const staggerT  = Math.min(1, t / 0.25);
    const collapseT = Math.min(1, Math.max(0, (t - 0.25) / 0.40));
    const dissolveT = Math.min(1, Math.max(0, (t - 0.65) / 0.35));

    const staggerRock = Math.sin(staggerT * Math.PI * 3) * (1 - staggerT) * 0.08;
    const tiltAngle = easeIn(collapseT) * 0.25 * facing;
    const sinkY = easeOut(collapseT) * 12;
    const squishY = 1 - easeOut(collapseT) * 0.45;
    const squishX = 1 + easeOut(collapseT) * 0.15;
    const alpha = dissolveT > 0 ? 1 - easeIn(dissolveT) : 1;
    const dissolveRise = easeOut(dissolveT) * 8;
    const deathProgress = Math.min(1, (collapseT * 0.5 + dissolveT * 0.5));

    // ── Render to offscreen canvas at full alpha ──
    offCtx.clearRect(0, 0, ds, ds);
    offCtx.save();
    offCtx.translate(halfDS, halfDS);
    offCtx.rotate(staggerRock + tiltAngle);
    offCtx.scale(squishX, squishY);
    if (deathProgress > 0.1) {
      offCtx.filter = `saturate(${Math.round((1 - deathProgress * 0.7) * 100)}%)`;
    }

    const rc: CreatureRenderContext = {
      ctx: offCtx as unknown as CanvasRenderingContext2D,
      species,
      pose: creature.pose,
      rootPos: { x: 0, y: 0 },
      scale: 1.0,
      facing,
      time: nowSec,
      quality,
      isWalking: false,
      creatureId: creature.id,
      playerTileX: staggerT < 1 ? state.player.position.tileX : undefined,
      playerTileY: staggerT < 1 ? state.player.position.tileY : undefined,
      creatureTileX: staggerT < 1 ? creature.position.tileX : undefined,
      creatureTileY: staggerT < 1 ? creature.position.tileY : undefined,
    };
    try { renderCreature(rc); } catch { /* isolate dying creature render failure */ }

    offCtx.filter = "none";
    offCtx.restore();

    // ── Composite to main canvas with death fade alpha ──
    mainCtx.save();
    mainCtx.translate(screenX, screenY);
    mainCtx.scale(zoom, zoom);
    mainCtx.globalAlpha = alpha;
    mainCtx.drawImage(this.deathCanvas, -halfDS, -halfDS + sinkY - dissolveRise);
    mainCtx.restore();

    // ── Spirit wisps ──
    if (t > 0.4) {
      const wispT = Math.min(1, (t - 0.4) / 0.6);
      const wispCount = 8;
      const seed = creature.id.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
      mainCtx.save();
      mainCtx.translate(screenX, screenY);
      mainCtx.scale(zoom, zoom);

      for (let i = 0; i < wispCount; i++) {
        const wSeed = (seed + i * 37) % 100 / 100;
        const wDelay = wSeed * 0.4;
        const wT = Math.min(1, Math.max(0, (wispT - wDelay) / (1 - wDelay)));
        if (wT <= 0) continue;

        const wEase = easeOut(wT);
        const wx = (wSeed - 0.5) * 20 + Math.sin(wT * Math.PI * 2 + i) * 4;
        const wy = -wEase * 35 - wSeed * 10;
        const wAlpha = wT < 0.3 ? wT / 0.3 : 1 - ((wT - 0.3) / 0.7);
        const wR = (2 + wSeed * 2) * (1 - wT * 0.5);

        mainCtx.globalAlpha = wAlpha * 0.6;
        const palColor = species.palette.body;
        const r = parseInt(palColor.slice(1, 3), 16);
        const g = parseInt(palColor.slice(3, 5), 16);
        const b = parseInt(palColor.slice(5, 7), 16);
        const wr = Math.round(r + (255 - r) * 0.7);
        const wg = Math.round(g + (255 - g) * 0.7);
        const wb = Math.round(b + (255 - b) * 0.7);

        const wGrad = mainCtx.createRadialGradient(wx, wy, 0, wx, wy, wR * 2);
        wGrad.addColorStop(0, `rgba(${wr},${wg},${wb},0.8)`);
        wGrad.addColorStop(0.5, `rgba(${wr},${wg},${wb},0.3)`);
        wGrad.addColorStop(1, `rgba(${wr},${wg},${wb},0)`);

        mainCtx.fillStyle = wGrad;
        mainCtx.beginPath();
        mainCtx.arc(wx, wy, wR * 2, 0, Math.PI * 2);
        mainCtx.fill();
      }
      mainCtx.restore();
    }
  }

  private drawFurnitureEntity(
    timestamp: number,
    placement: FurniturePlacement,
    screenX: number,
    screenY: number
  ): void {
    const registryId    = toRegistryId(placement.type);
    const furnitureType = getFurnitureType(registryId);

    if (furnitureType) {
      const rotation = getVisualRotation(placement.type, placement.direction);
      renderFurniture(
        this.ctx,
        furnitureType,
        screenX,
        screenY,
        rotation,
        placement.state,
        timestamp
      );
    } else {
      // Graceful fallback — warm neutral block
      this.drawFurnitureFallback(screenX, screenY);
    }
  }

  private drawFurnitureFallback(screenX: number, screenY: number): void {
    const { ctx } = this;
    ctx.save();
    ctx.globalAlpha = 0.85;
    ctx.fillStyle   = "#8a7a6a";
    ctx.fillRect(Math.round(screenX) - 16, Math.round(screenY) - 32, 32, 32);
    ctx.fillStyle   = "#6a5a4a";
    ctx.fillRect(Math.round(screenX) - 16, Math.round(screenY) - 32, 32, 4);
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  /**
   * Compute a subtle tint color for living entities based on room mood + time of day.
   * Returns an rgba string, or null if no tint needed (neutral midday light).
   */
  private getEntityLightTint(
    mood: MoodLighting, gameHour: number,
    entityWx: number = 0, entityWy: number = 0,
    room?: import("../engine/state.js").Room
  ): string | null {
    // Time-of-day warmth: warm orange at dawn/dusk, cool blue at night, neutral midday
    let r = 0, g = 0, b = 0, a = 0;

    if (gameHour >= 20 || gameHour < 5) {
      r = 30; g = 40; b = 80; a = 0.08;
    } else if (gameHour >= 5 && gameHour < 7) {
      const t = (gameHour - 5) / 2;
      r = 100; g = 60 + t * 20; b = 40; a = 0.07 * (1 - t);
    } else if (gameHour >= 17 && gameHour < 20) {
      const t = (gameHour - 17) / 3;
      r = 110; g = 60; b = 20; a = 0.06 * (1 - t * 0.5);
    } else {
      a = 0.02; r = 80; g = 70; b = 60;
    }

    // Blend with mood tint
    const moodStr = mood.tint;
    const match = moodStr.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (match) {
      const mr = parseInt(match[1]);
      const mg = parseInt(match[2]);
      const mb = parseInt(match[3]);
      r = Math.round(r * 0.7 + mr * 0.3);
      g = Math.round(g * 0.7 + mg * 0.3);
      b = Math.round(b * 0.7 + mb * 0.3);
      a = Math.min(0.15, a + 0.02);
    }

    // Proximity warmth from lit fireplaces and lamps
    if (room) {
      let warmBoost = 0;
      for (const f of room.furniture) {
        const isFireplace = f.type === "fireplace" && f.state["lit"] !== false;
        const isLamp = (f.type === "floor_lamp" || f.type === "lamp") && f.state["on"] !== false;
        if (!isFireplace && !isLamp) continue;
        const dx = entityWx - (f.tileX + 0.5);
        const dy = entityWy - (f.tileY + 0.5);
        const dist = Math.sqrt(dx * dx + dy * dy);
        const radius = isFireplace ? 4 : 3;
        if (dist < radius) {
          const falloff = 1 - dist / radius;
          const intensity = isFireplace ? 0.12 : 0.06;
          warmBoost += falloff * falloff * intensity;
        }
      }
      if (warmBoost > 0) {
        // Shift tint toward warm amber based on proximity
        warmBoost = Math.min(warmBoost, 0.2);
        r = Math.round(r * (1 - warmBoost * 3) + 255 * warmBoost * 3);
        g = Math.round(g * (1 - warmBoost * 2) + 140 * warmBoost * 2);
        b = Math.round(b * (1 - warmBoost * 4) + 40 * warmBoost * 4);
        a = Math.min(0.18, a + warmBoost);
      }
    }

    if (a < 0.01) return null;
    r = Math.max(0, Math.min(255, r));
    g = Math.max(0, Math.min(255, g));
    b = Math.max(0, Math.min(255, b));
    return `rgba(${r},${g},${b},${a.toFixed(3)})`;
  }

  /**
   * Draw the local player using the character composer.
   *
   * Syncs animation state and direction from game state, advances frame timing
   * through updateAnimation, and delegates all pixel rendering to renderCharacter.
   */
  private drawPlayerEntity(
    dt: number,
    state: GameState,
    screenX: number,
    screenY: number
  ): void {
    const charState = this.charState;
    if (!charState) return;

    // ── Appearance sync — detect wardrobe/external changes ─────────────
    // The bridge's composer appearance can be updated by the wardrobe app,
    // character creation, or any other system. If the version has changed,
    // rebuild the charState's appearance from the bridge and invalidate cache.
    const appVer = getAppearanceVersion();
    if (appVer !== this.cachedAppearanceVersion) {
      const fresh = toComposerAppearance();
      // Preserve device-swapped heldItem if device is active
      if (this.deviceHeldItemActive) {
        fresh.heldItem = charState.appearance.heldItem;
      }
      charState.appearance = fresh;
      invalidateCharacter(charState);
      this.cachedAppearanceVersion = appVer;
    }

    const p            = state.player;
    const dev          = p.device;
    const composerAnim = toComposerAnim(p.animState);
    const composerDir  = toComposerDir(p.direction);

    // ── Device held-item swap ──────────────────────────────────────────
    // When device is active, swap the character's heldItem to show the
    // physical phone/pad in their hand. Restore original when stowed.
    const deviceActive = dev.activeDevice !== null && dev.animPhase !== "stowed";
    if (deviceActive && !this.deviceHeldItemActive) {
      // Entering device mode — save current item, swap in device
      this.savedHeldItem = charState.appearance.heldItem;
      charState.appearance.heldItem = dev.activeDevice === "pad" ? 7 : 6;
      invalidateCharacter(charState);
      this.deviceHeldItemActive = true;
      // Feed device color and case to the composer
      const color = dev.activeDevice === "pad" ? dev.hPad.color : dev.hPhone.color;
      const devCfg = dev.activeDevice === "pad" ? dev.hPad : dev.hPhone;
      setDeviceContext(color, dev.activeDevice);
      setDeviceCaseContext(devCfg.case ?? null);
    } else if (!deviceActive && this.deviceHeldItemActive) {
      // Exiting device mode — restore original held item
      charState.appearance.heldItem = this.savedHeldItem;
      invalidateCharacter(charState);
      this.deviceHeldItemActive = false;
      setDeviceContext("#3a3a3a", null);
      setDeviceCaseContext(null);
    } else if (deviceActive) {
      // Refresh device context each frame (color may change, type may swap)
      const color = dev.activeDevice === "pad" ? dev.hPad.color : dev.hPhone.color;
      const devCfg = dev.activeDevice === "pad" ? dev.hPad : dev.hPhone;
      setDeviceContext(color, dev.activeDevice);
      setDeviceCaseContext(devCfg.case ?? null);
    }

    charState.animState = composerAnim;
    charState.direction = composerDir;

    // Set emote and buff aura context for the composer
    setEmoteContext(p.activeEmoteId);
    setBuffAuraContext(p.buffAuraColor, p.buffAuraIntensity);

    // Pass moveProgress so walk animation syncs to actual movement
    updateAnimation(charState, dt, p.moveProgress);

    // Propagate animation state transitions back to game state.
    // updateAnimation handles auto-transitions (e.g. combat_attack → combat_idle
    // after 3 frames). Without this feedback, the game state stays "combat_attack"
    // forever and the AnimationController restarts the attack animation every frame.
    if (charState.animState !== composerAnim) {
      state.player.animState = charState.animState;
    }

    // AnimationController handles blink / idle variations independently
    this.animController.setState(charState.animState);
    this.animController.setDirection(composerDir);
    // Sync walk frame to tile traversal so legs match movement exactly
    if (p.moveProgress !== undefined && p.moveProgress > 0 &&
        (composerAnim === "walk" || composerAnim === "device_walk")) {
      this.animController.setMoveProgress(p.moveProgress);
    }
    this.animController.update(dt);

    const snap = this.animController.getSnapshot();

    // ── Attention engine update ────────────────────────────────────────
    // Compute gaze targets, blink phase, hair swing, and secondary motion.
    // This makes the character feel alive — eyes track cursor, hair sways
    // with movement, blinks happen naturally, accessories lag behind.
    {
      // Gather attention targets — priority system
      const attTargets: import("../character/attention.js").AttentionTarget[] = [];

      // Cursor tracking — direct line of attention. The character's gaze
      // follows the mouse: world position when hovering tiles, upward when
      // hovering GUI elements. Priority 6 = above ambient NPC/creature
      // awareness, below conversation (8) and combat lock (10).
      if (this.mouseX !== 0 || this.mouseY !== 0) {
        // Eye offset from feet in canvas pixels, accounting for body type and zoom.
        // Body anchor eyeY: slim/avg/broad=19, petite=35. Ground contact=136.
        const bodyType = charState.appearance.bodyType ?? 1;
        const eyeYs = [19, 19, 19, 35]; // indexed by body type
        const eyeOffset = -(136 - (eyeYs[bodyType] ?? 19)) * this.cameraZoom * 0.5;
        const cg = cursorToGaze(this.playerCanvasX, this.playerCanvasY, this.mouseX, this.mouseY, eyeOffset);
        attTargets.push({ type: "cursor", screenX: cg.screenX, screenY: cg.screenY, priority: 6 });
      }

      // Combat target (highest priority — player stares down their opponent)
      const inCombat = state.player.combatState?.active && state.player.combatState?.targetId;
      if (inCombat) {
        const combatTarget = state.creatures.find(c => c.id === state.player.combatState.targetId);
        if (combatTarget) {
          const cg = worldToGaze(p.position.tileX, p.position.tileY, combatTarget.position.tileX, combatTarget.position.tileY, p.direction);
          attTargets.push({ type: "entity", screenX: cg.screenX, screenY: cg.screenY, priority: 10 });
        }
      }

      // Nearby NPCs (ambient awareness — cursor overrides)
      if (state.npcs) {
        for (const npc of state.npcs) {
          const dist = Math.abs(npc.position.tileX - p.position.tileX) + Math.abs(npc.position.tileY - p.position.tileY);
          if (dist <= 4) {
            const ng = worldToGaze(p.position.tileX, p.position.tileY, npc.position.tileX, npc.position.tileY, p.direction);
            const npcPriority = inCombat ? 1 : 3 + (4 - dist) * 0.5;
            attTargets.push({ type: "entity", screenX: ng.screenX, screenY: ng.screenY, priority: npcPriority });
          }
        }
      }

      // Nearby creatures (ambient awareness — glance at pets, wildlife)
      for (const creature of state.creatures) {
        if (creature.mountState?.mounted) continue;
        const dist = Math.abs(creature.position.tileX - p.position.tileX) + Math.abs(creature.position.tileY - p.position.tileY);
        if (dist <= 4) {
          const cg = worldToGaze(p.position.tileX, p.position.tileY, creature.position.tileX, creature.position.tileY, p.direction);
          const crPriority = inCombat ? 0.5 : 2 + (4 - dist) * 0.3;
          attTargets.push({ type: "entity", screenX: cg.screenX, screenY: cg.screenY, priority: crPriority });
        }
      }

      // Character velocity for hair/accessory dynamics
      const charVelX = (p.moveProgress !== undefined && p.moveProgress > 0)
        ? ((p.direction === "E" || p.direction === "NE" || p.direction === "SE") ? 60 :
           (p.direction === "W" || p.direction === "NW" || p.direction === "SW") ? -60 : 0)
        : 0;
      const charVelY = (p.moveProgress !== undefined && p.moveProgress > 0)
        ? ((p.direction === "S" || p.direction === "SE" || p.direction === "SW") ? 30 :
           (p.direction === "N" || p.direction === "NE" || p.direction === "NW") ? -30 : 0)
        : 0;

      updateAttention(
        this.attentionState, dt, attTargets,
        charVelX, charVelY,
        deviceActive,
        snap.lookOffset, snap.weightOffset
      );

      // Push full attention state to composer — every layer of living presence
      const att = this.attentionState;
      setAttentionContext(
        att.gazeX, att.gazeY,
        att.headTiltX, att.headTiltY,
        att.blinkPhase,
        att.hairSwingX, att.hairSettlePhase,
        att.secondaryLagX, att.secondaryLagY,
        att.shoulderTurnX, att.breathIntensity, att.interestLevel,
        att.pupilDilation, att.lidFollowY,
        att.microTremorX, att.microTremorY,
        att.browEngagement,
        att.fidgetArmDY, att.fidgetTorsoX, att.weightShiftPhase,
        att.headYaw, att.headPitch
      );

      // ── Expose gaze direction to minimap ──
      // Convert screen-space gaze to tile-space angle for minimap attention cone.
      // Inverse iso: tileDX = gazeX/2 + gazeY, tileDY = -gazeX/2 + gazeY
      const gx = att.gazeX, gy = att.gazeY;
      const tileDX = gx * 0.5 + gy;
      const tileDY = -gx * 0.5 + gy;
      if (Math.abs(tileDX) > 0.01 || Math.abs(tileDY) > 0.01) {
        state.player.gazeAngle = Math.atan2(tileDY, tileDX);
      }
      state.player.gazeFocus = att.interestLevel;
    }

    // ── Weapon & shield context ─────────────────────────────────────────
    // Push equipped weapon/shield visuals to the composer so the character
    // renders with their equipped gear visible.
    {
      // Prefer visual data baked into equipmentData (new items), fall back
      // to name-based lookup (pre-existing items without weaponVisual field).
      const wpn = state.player.equipment.weapon?.equipmentData?.weaponVisual
        ?? getWeaponVisual(state);
      const shd = state.player.equipment.shield?.equipmentData?.shieldVisual
        ?? getShieldVisual(state);
      setWeaponContext(wpn, shd);
      setStaggerContext(state.player.combatState.staggerTimer);
    }

    // ── Device screen glow ─────────────────────────────────────────────
    // When holding a device, cast a subtle blue-white glow onto the
    // character's face and upper body — like a real screen in a dim room.
    if (deviceActive && dev.animPhase === "held") {
      const { ctx } = this;
      ctx.save();
      ctx.globalCompositeOperation = "screen";
      const glowX = screenX;
      const glowY = screenY - 30; // chest-height where screen is held
      const glowR = 28;
      const glow = ctx.createRadialGradient(glowX, glowY, 0, glowX, glowY, glowR);
      glow.addColorStop(0, "rgba(170,190,255,0.07)");
      glow.addColorStop(0.5, "rgba(170,190,255,0.03)");
      glow.addColorStop(1, "rgba(170,190,255,0)");
      ctx.fillStyle = glow;
      ctx.fillRect(glowX - glowR, glowY - glowR, glowR * 2, glowR * 2);
      ctx.restore();
    }

    // ── Buff aura — glowing ring beneath character when buffed ──
    if (p.buffAuraColor && p.buffAuraIntensity > 0) {
      const { ctx } = this;
      ctx.save();
      ctx.globalCompositeOperation = "screen";
      const pulse = 0.7 + 0.3 * Math.sin(performance.now() / 400);
      ctx.globalAlpha = p.buffAuraIntensity * 0.25 * pulse;
      const auraR = 20;
      const auraGrad = ctx.createRadialGradient(screenX, screenY + 6, 2, screenX, screenY + 6, auraR);
      auraGrad.addColorStop(0, p.buffAuraColor);
      auraGrad.addColorStop(0.6, p.buffAuraColor);
      auraGrad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = auraGrad;
      ctx.beginPath();
      ctx.ellipse(screenX, screenY + 6, auraR, auraR * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // ── Flow state aura — gold halo when momentum crosses the threshold ──
    // When the player is "in flow" (momentum.value >= 0.65 w/ hysteresis),
    // an animated warm aura wraps the character: a soft beneath-feet glow
    // plus a subtle body-centered halo that pulses at ~2.6 Hz. The
    // threshold crossing is what makes the Momentum ODE from dynamics.ts
    // FEEL different on screen, not just on the HUD meter.
    {
      const momentum = getMomentumState();
      if (momentum.inFlow) {
        const { ctx } = this;
        const tSec = performance.now() / 1000;
        // Ease the aura in as the player climbs above the threshold —
        // intensity tracks how far past 0.65 momentum currently is.
        const over = Math.max(0, Math.min(1, (momentum.value - 0.55) / 0.35));
        const pulse = 0.7 + 0.3 * Math.sin(tSec * Math.PI * 2.6);

        ctx.save();
        ctx.globalCompositeOperation = "screen";

        // Ground aura — a wide warm ellipse beneath the feet
        ctx.globalAlpha = 0.22 * over * pulse;
        const groundR = 24;
        const groundGrad = ctx.createRadialGradient(
          screenX, screenY + 8, 2,
          screenX, screenY + 8, groundR,
        );
        groundGrad.addColorStop(0, "rgba(255,220,120,0.9)");
        groundGrad.addColorStop(0.5, "rgba(255,180,60,0.4)");
        groundGrad.addColorStop(1, "rgba(255,140,40,0)");
        ctx.fillStyle = groundGrad;
        ctx.beginPath();
        ctx.ellipse(screenX, screenY + 8, groundR, groundR * 0.5, 0, 0, Math.PI * 2);
        ctx.fill();

        // Body halo — a tighter glow centered on the torso
        ctx.globalAlpha = 0.18 * over * (0.8 + pulse * 0.2);
        const bodyR = 26;
        const bodyGrad = ctx.createRadialGradient(
          screenX, screenY - 18, 0,
          screenX, screenY - 18, bodyR,
        );
        bodyGrad.addColorStop(0, "rgba(255,236,160,0.85)");
        bodyGrad.addColorStop(0.5, "rgba(255,200,80,0.25)");
        bodyGrad.addColorStop(1, "rgba(255,180,60,0)");
        ctx.fillStyle = bodyGrad;
        ctx.beginPath();
        ctx.arc(screenX, screenY - 18, bodyR, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
      }
    }

    renderCharacter(
      this.ctx,
      charState,
      screenX,
      screenY,
      composerDir,
      snap.frame,
      snap.isBlinking,
      snap.bobOffset
    );

    // ── Emote bubble — subtle floating label above character during emotes ──
    if (p.activeEmoteId) {
      const { ctx } = this;
      const emoteAge = performance.now() / 1000 - (p.activeEmoteUntil - 2); // approx age
      const fadeIn = Math.min(1, emoteAge * 3); // fast fade in
      const bubbleAlpha = fadeIn > 0 ? fadeIn : 0;
      if (bubbleAlpha > 0) {
        ctx.save();
        ctx.globalAlpha = bubbleAlpha * 0.85;
        // Find emote name
        const emoteNames: Record<string, string> = {
          wave: "Wave", bow: "Bow", salute: "Salute", clap: "Clap",
          surprised: "Surprised!", shocked: "Shocked!", laugh: "Haha!", cry: "...",
          dance: "Dance", groove: "Groove", spin: "Spin!", air_guitar: "Rock!",
          think: "Hmm...", shrug: "Shrug", flex: "Flex!", sleep: "Zzz...",
        };
        const label = emoteNames[p.activeEmoteId] ?? p.activeEmoteId;
        ctx.font = "bold 9px monospace";
        const tw = ctx.measureText(label).width;
        const bw = tw + 10;
        const bh = 16;
        const bx = screenX - bw / 2;
        const by = screenY - 52; // above character head

        // Subtle rounded bubble
        ctx.fillStyle = "rgba(14, 10, 4, 0.7)";
        ctx.beginPath();
        const rr = 4;
        ctx.moveTo(bx + rr, by);
        ctx.lineTo(bx + bw - rr, by);
        ctx.arcTo(bx + bw, by, bx + bw, by + rr, rr);
        ctx.lineTo(bx + bw, by + bh - rr);
        ctx.arcTo(bx + bw, by + bh, bx + bw - rr, by + bh, rr);
        // Tiny tail pointing down
        ctx.lineTo(screenX + 3, by + bh);
        ctx.lineTo(screenX, by + bh + 4);
        ctx.lineTo(screenX - 3, by + bh);
        ctx.lineTo(bx + rr, by + bh);
        ctx.arcTo(bx, by + bh, bx, by + bh - rr, rr);
        ctx.lineTo(bx, by + rr);
        ctx.arcTo(bx, by, bx + rr, by, rr);
        ctx.closePath();
        ctx.fill();

        // Border
        ctx.strokeStyle = "rgba(107, 79, 12, 0.5)";
        ctx.lineWidth = 0.75;
        ctx.stroke();

        // Text
        ctx.fillStyle = "#f0d88a";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(label, screenX, by + bh / 2);
        ctx.restore();
      }
    }

    // ── Hit flash — brief bright overlay when player takes damage ──
    if (state.player.combatState.staggerTimer > 0.15) {
      const { ctx } = this;
      const flashAlpha = Math.min((state.player.combatState.staggerTimer - 0.15) / 0.1, 1.0) * 0.25;
      ctx.save();
      ctx.globalCompositeOperation = "screen";
      ctx.globalAlpha = flashAlpha;
      ctx.fillStyle = "#FF4444";
      // Full character bounds: composer outputs at 0.5× scale so screen size is CHAR_W/2 × CHAR_H/2
      const halfW = CHAR_W * 0.25;
      const halfH = CHAR_H * 0.25;
      const drawX = Math.round(screenX - halfW);
      const drawY = Math.round(screenY - halfH * 1.6 + snap.bobOffset);
      ctx.fillRect(drawX, drawY, halfW * 2, halfH * 2);
      ctx.restore();
    }

    // ── Post-render screen glow (on top of character) ──────────────────
    // A second, brighter glow pass drawn ON the character for the
    // screen light illuminating their face from below.
    if (deviceActive && dev.animPhase === "held") {
      const { ctx } = this;
      ctx.save();
      ctx.globalCompositeOperation = "screen";
      const glowX = screenX;
      const glowY = screenY - 38; // face height
      const glow = ctx.createRadialGradient(glowX, glowY, 0, glowX, glowY, 18);
      glow.addColorStop(0, "rgba(190,210,255,0.05)");
      glow.addColorStop(1, "rgba(190,210,255,0)");
      ctx.fillStyle = glow;
      ctx.fillRect(glowX - 18, glowY - 18, 36, 36);
      ctx.restore();
    }

    // Clear weapon/stagger context so NPCs don't inherit player's equipment
    setWeaponContext(null, null);
    setStaggerContext(0);
  }

  // ── NPC Rendering ────────────────────────────────────────────────────────

  private drawNpcEntity(
    dt: number,
    state: GameState,
    npcData: NpcState,
    screenX: number,
    screenY: number,
  ): void {
    // Lazily create character state for this NPC
    let npcChar = this.npcCharStates.get(npcData.id);
    if (!npcChar) {
      npcChar = createCharacter(npcToComposerAppearance(npcData.appearance));
      this.npcCharStates.set(npcData.id, npcChar);
    }

    // Lazily create attention state for this NPC
    let npcAtt = this.npcAttentionStates.get(npcData.id);
    if (!npcAtt) {
      npcAtt = createAttentionState();
      this.npcAttentionStates.set(npcData.id, npcAtt);
    }

    const composerDir = toComposerDir(npcData.direction);
    const composerAnim = toComposerAnim(npcData.animState);
    npcChar.animState = composerAnim;
    npcChar.direction = composerDir;

    updateAnimation(npcChar, dt, 0);

    // ── NPC attention update ──────────────────────────────────────────
    // NPCs get natural blink, idle saccades, and gaze toward nearby
    // player, creatures, and other NPCs — a living, aware character.
    {
      const attTargets: import("../character/attention.js").AttentionTarget[] = [];

      // NPC gazes toward the player if they're within 5 tiles
      const p = state.player;
      const dist = Math.abs(npcData.position.tileX - p.position.tileX)
                 + Math.abs(npcData.position.tileY - p.position.tileY);
      if (dist <= 5) {
        const ng = worldToGaze(
          npcData.position.tileX, npcData.position.tileY,
          p.position.tileX, p.position.tileY,
          npcData.direction
        );
        attTargets.push({ type: "entity", screenX: ng.screenX, screenY: ng.screenY, priority: 2 + (5 - dist) * 0.5 });
      }

      // If NPC is in conversation, lock gaze on player with high priority
      if (npcData.inConversation) {
        const cg = worldToGaze(
          npcData.position.tileX, npcData.position.tileY,
          p.position.tileX, p.position.tileY,
          npcData.direction
        );
        attTargets.push({ type: "conversation", screenX: cg.screenX, screenY: cg.screenY, priority: 8 });
      }

      // NPCs notice nearby creatures (glancing at a passing cat, etc.)
      for (const creature of state.creatures) {
        if (creature.mountState?.mounted) continue;
        const cDist = Math.abs(creature.position.tileX - npcData.position.tileX)
                    + Math.abs(creature.position.tileY - npcData.position.tileY);
        if (cDist <= 3) {
          const cg = worldToGaze(
            npcData.position.tileX, npcData.position.tileY,
            creature.position.tileX, creature.position.tileY,
            npcData.direction
          );
          attTargets.push({ type: "entity", screenX: cg.screenX, screenY: cg.screenY, priority: 1.5 + (3 - cDist) * 0.3 });
        }
      }

      // NPCs notice other nearby NPCs
      if (state.npcs) {
        for (const otherNpc of state.npcs) {
          if (otherNpc.id === npcData.id) continue;
          const nDist = Math.abs(otherNpc.position.tileX - npcData.position.tileX)
                      + Math.abs(otherNpc.position.tileY - npcData.position.tileY);
          if (nDist <= 3) {
            const ng = worldToGaze(
              npcData.position.tileX, npcData.position.tileY,
              otherNpc.position.tileX, otherNpc.position.tileY,
              npcData.direction
            );
            attTargets.push({ type: "entity", screenX: ng.screenX, screenY: ng.screenY, priority: 1.5 + (3 - nDist) * 0.3 });
          }
        }
      }

      // NPC walking velocity for hair/accessory dynamics
      const npcWalking = npcData.animState === "walk";
      const npcVelX = npcWalking
        ? ((npcData.direction === "E" || npcData.direction === "NE" || npcData.direction === "SE") ? 40 :
           (npcData.direction === "W" || npcData.direction === "NW" || npcData.direction === "SW") ? -40 : 0)
        : 0;
      const npcVelY = npcWalking
        ? ((npcData.direction === "S" || npcData.direction === "SE" || npcData.direction === "SW") ? 20 :
           (npcData.direction === "N" || npcData.direction === "NE" || npcData.direction === "NW") ? -20 : 0)
        : 0;

      updateAttention(npcAtt, dt, attTargets, npcVelX, npcVelY, false, 0, 0);

      // Expose NPC gaze to minimap
      const ngx = npcAtt.gazeX, ngy = npcAtt.gazeY;
      const ntDX = ngx * 0.5 + ngy;
      const ntDY = -ngx * 0.5 + ngy;
      if (Math.abs(ntDX) > 0.01 || Math.abs(ntDY) > 0.01) {
        npcData.gazeAngle = Math.atan2(ntDY, ntDX);
      }
      npcData.gazeFocus = npcAtt.interestLevel;

      setAttentionContext(
        npcAtt.gazeX, npcAtt.gazeY,
        npcAtt.headTiltX, npcAtt.headTiltY,
        npcAtt.blinkPhase,
        npcAtt.hairSwingX, npcAtt.hairSettlePhase,
        npcAtt.secondaryLagX, npcAtt.secondaryLagY,
        npcAtt.shoulderTurnX, npcAtt.breathIntensity, npcAtt.interestLevel,
        npcAtt.pupilDilation, npcAtt.lidFollowY,
        npcAtt.microTremorX, npcAtt.microTremorY,
        npcAtt.browEngagement,
        npcAtt.fidgetArmDY, npcAtt.fidgetTorsoX, npcAtt.weightShiftPhase,
        npcAtt.headYaw, npcAtt.headPitch
      );
    }

    // Derive blink from attention — blinkPhase > 0.3 means eyelids are substantially closed
    const npcBlinking = npcAtt.isBlinking;

    renderCharacter(
      this.ctx,
      npcChar,
      screenX,
      screenY,
      composerDir,
      0,       // frame 0 — idle
      npcBlinking,
      0        // no bob
    );

    // Reset attention context so it doesn't bleed into the next entity
    setAttentionContext(0, 0, 0, 0, 0, 0, 0, 0, 0);

    // Name label above NPC
    const { ctx } = this;
    const labelY = screenY - 50;
    ctx.save();
    ctx.font = "bold 10px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";

    // Name
    ctx.fillStyle = "rgba(0,0,0,0.5)";
    ctx.fillText(npcData.name, screenX + 1, labelY + 1);
    ctx.fillStyle = "#fff";
    ctx.fillText(npcData.name, screenX, labelY);

    // Title underneath
    ctx.font = "9px sans-serif";
    ctx.fillStyle = "rgba(0,0,0,0.4)";
    ctx.fillText(npcData.title, screenX + 1, labelY + 11);
    ctx.fillStyle = "#c8a050";
    ctx.fillText(npcData.title, screenX, labelY + 10);

    ctx.restore();

    // ── Quest giver mark ────────────────────────────────────────────────────────
    // Gold/silver ! or ? floating above quest-relevant NPCs.
    if (npcData.questMark) {
      const markY = labelY - 22;
      drawQuestGiverMark(ctx, npcData.questMark, screenX, markY, performance.now() / 1000);
    }

    // ── NPC interaction prompt ──────────────────────────────────────────────────────
    // Subtle indicator when the player is close enough to talk.
    // "Come talk to me" — inviting, not urgent.
    if (!npcData.inConversation && this._state) {
      const p = this._state.player.position;
      const pdx = npcData.position.tileX - p.tileX;
      const pdy = npcData.position.tileY - p.tileY;
      const pdist = Math.sqrt(pdx * pdx + pdy * pdy);

      if (pdist <= 3) {
        // Proximity factor: fully visible at distance 0–2, fades out at 3
        const proximityAlpha = pdist <= 2 ? 1 : 1 - (pdist - 2);
        const pnow = performance.now() / 1000;

        // Gentle bob — slow and soft, like a breath
        const pbob = Math.sin(pnow * 1.8) * 2;

        const indicatorY = labelY - 16 + pbob;

        ctx.save();
        ctx.globalAlpha = proximityAlpha * 0.7;

        if (npcData.role === "merchant") {
          // Merchant: small gold coin icon
          this.drawMerchantIcon(ctx, screenX, indicatorY);
        } else {
          // Others: "..." speech bubble — come chat
          this.drawSpeechPrompt(ctx, screenX, indicatorY, pnow);
        }

        ctx.restore();
      }
    }
  }

  /**
   * Draw a tiny "..." speech prompt above an NPC.
   * Three dots that pulse gently in sequence — alive, patient, waiting.
   */
  private drawSpeechPrompt(
    ctx: CanvasRenderingContext2D,
    x: number, y: number,
    now: number,
  ): void {
    // Background pill
    const pillW = 22;
    const pillH = 12;
    const px = x - pillW / 2;
    const py = y - pillH / 2;

    ctx.fillStyle = "rgba(255, 250, 240, 0.85)";
    ctx.beginPath();
    ctx.moveTo(px + 4, py);
    ctx.lineTo(px + pillW - 4, py);
    ctx.arcTo(px + pillW, py, px + pillW, py + 4, 4);
    ctx.lineTo(px + pillW, py + pillH - 4);
    ctx.arcTo(px + pillW, py + pillH, px + pillW - 4, py + pillH, 4);
    ctx.lineTo(px + 4, py + pillH);
    ctx.arcTo(px, py + pillH, px, py + pillH - 4, 4);
    ctx.lineTo(px, py + 4);
    ctx.arcTo(px, py, px + 4, py, 4);
    ctx.closePath();
    ctx.fill();

    // Tiny tail pointing down
    ctx.beginPath();
    ctx.moveTo(x - 2, py + pillH);
    ctx.lineTo(x, py + pillH + 3);
    ctx.lineTo(x + 2, py + pillH);
    ctx.closePath();
    ctx.fill();

    // Three dots — each pulses with a staggered phase
    const dotR = 1.5;
    const dotSpacing = 5;
    const dotBaseY = y;

    for (let i = 0; i < 3; i++) {
      const phase = now * 2 + i * 0.6;
      const dotAlpha = 0.35 + Math.sin(phase) * 0.25;
      const dotX = x - dotSpacing + i * dotSpacing;

      ctx.fillStyle = `rgba(90, 70, 50, ${dotAlpha.toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(dotX, dotBaseY, dotR, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  /**
   * Draw a small gold coin icon for merchant NPCs.
   * A warm golden circle with a simple embossed look.
   */
  private drawMerchantIcon(
    ctx: CanvasRenderingContext2D,
    x: number, y: number,
  ): void {
    const coinRadius = 6;

    // Gold coin body
    ctx.beginPath();
    ctx.arc(x, y, coinRadius, 0, Math.PI * 2);
    ctx.fillStyle = "#d4a030";
    ctx.fill();

    // Darker rim
    ctx.strokeStyle = "#9b7318";
    ctx.lineWidth = 1;
    ctx.stroke();

    // Inner highlight (top half)
    ctx.beginPath();
    ctx.arc(x, y - 1, coinRadius - 2, Math.PI, 0);
    ctx.fillStyle = "rgba(255, 220, 120, 0.5)";
    ctx.fill();

    // "G" text for gold
    ctx.font = "bold 7px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#6b4f0c";
    ctx.fillText("G", x, y + 0.5);
  }

  /**
   * Draw per-furniture special effects on top of each sprite.
   *
   * Kept and enhanced from the original version:
   *   - Aeterna: pulsing blue glow + orbiting particles
   *   - Floor lamp: warm radial glow with subtle flicker + floor pool
   *   - Fern: leaves swaying in gentle wind
   *   - Fireplace: dancing amber light on nearby floor tiles + embers
   */
  private drawFurnitureEffects(
    timestamp: number,
    placement: FurniturePlacement,
    screenX: number,
    screenY: number
  ): void {
    const { ctx } = this;
    const t    = timestamp / 1000;
    const type = placement.type;

    // ── Aeterna device — presence-state-driven glow ─────────────────────────
    if (type === "aeterna" || type === "aeterna_device") {
      const presence = this._state?.aeterna;
      const v = presence
        ? getPresenceVisuals(presence, timestamp)
        : null;

      const [r, g, b, ga] = v?.glowColor  ?? [80, 160, 255, 0.35];
      const [cr, cg, cb]  = v?.coreColor  ?? [180, 220, 255];
      const glowR   = v?.glowR         ?? 28;
      const pulse   = v?.pulse         ?? 0.55;
      const nOrb    = v?.orbitCount    ?? 4;
      const oSpeed  = v?.orbitSpeed    ?? 1.0;
      const oRadius = v?.orbitRadius   ?? 1.0;
      const pAlpha  = v?.particleAlpha ?? 0.55;
      const [pc1, pc2] = v?.particleColors ?? ["#a0d0ff", "#c0e8ff"];
      const cy = screenY - 20;

      ctx.save();
      ctx.globalCompositeOperation = "screen";

      const outerGlow = ctx.createRadialGradient(screenX, cy, 2, screenX, cy, glowR);
      outerGlow.addColorStop(0,   `rgba(${r}, ${g}, ${b}, ${ga})`);
      outerGlow.addColorStop(0.5, `rgba(${r * 0.75 | 0}, ${g * 0.63 | 0}, ${b * 0.86 | 0}, ${ga * 0.45})`);
      outerGlow.addColorStop(1,   `rgba(${r * 0.5 | 0}, ${g * 0.38 | 0}, ${b * 0.7 | 0}, 0)`);
      ctx.fillStyle = outerGlow;
      ctx.beginPath();
      ctx.ellipse(screenX, cy, glowR, glowR * 0.6, 0, 0, Math.PI * 2);
      ctx.fill();

      const core = ctx.createRadialGradient(screenX, cy - 2, 0, screenX, cy - 2, 8);
      core.addColorStop(0, `rgba(${cr}, ${cg}, ${cb}, ${0.85 * pulse})`);
      core.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
      ctx.fillStyle = core;
      ctx.beginPath();
      ctx.arc(screenX, cy - 2, 8, 0, Math.PI * 2);
      ctx.fill();

      // Screen glow — subtle when listening/speaking
      if (v?.screenGlow) {
        ctx.globalAlpha = 0.18 * pulse;
        ctx.fillStyle   = v.screenGlowColor;
        ctx.beginPath();
        ctx.ellipse(screenX, cy + 4, 10, 5, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      }

      // Orbiting particles
      const baseOrbit = 14 * oRadius;
      for (let i = 0; i < nOrb; i++) {
        const angle  = t * oSpeed * 1.4 + (i / nOrb) * Math.PI * 2;
        const orbit  = baseOrbit + Math.sin(t * 2 + i * 1.3) * 3;
        const px     = screenX + Math.cos(angle) * orbit;
        const py     = cy      + Math.sin(angle) * orbit * 0.45;
        const palpha = pAlpha * (0.6 + Math.sin(t * 3 + i * 0.9) * 0.35);

        ctx.globalAlpha = palpha;
        ctx.fillStyle   = i % 2 === 0 ? pc1 : pc2;
        ctx.beginPath();
        ctx.arc(px, py, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "source-over";

      // Ambient thought text — typewriter reveal near the device
      if (v?.hasThought && v.thoughtRevealed.length > 0 && v.thoughtAlpha > 0.01) {
        const alpha = v.thoughtAlpha;
        ctx.globalAlpha = alpha;
        ctx.font        = "italic 9px monospace";
        ctx.textAlign   = "center";
        ctx.textBaseline = "bottom";
        // Soft glow halo behind text
        ctx.shadowColor  = `rgba(${r}, ${g}, ${b}, ${alpha * 0.8})`;
        ctx.shadowBlur   = 6;
        ctx.fillStyle    = `rgba(200, 235, 255, ${alpha})`;
        ctx.fillText(v.thoughtRevealed, screenX, cy - glowR * 0.5 - 4);
        ctx.shadowBlur   = 0;
        ctx.globalAlpha  = 1;
        ctx.textAlign    = "left";
        ctx.textBaseline = "alphabetic";
      }

      ctx.restore();
    }

    // ── Floor lamp — warm radial glow with subtle flicker ─────────────────
    if (type === "floor_lamp" || type === "lamp") {
      if (placement.state["on"] === false) return;

      const flicker  = 1 + Math.sin(t * 23) * 0.04 + Math.sin(t * 37) * 0.02;
      const glowSize = (55 + Math.sin(t * 1.1) * 4) * flicker;
      const cy       = screenY - 10;

      ctx.save();
      ctx.globalCompositeOperation = "screen";

      const lampGlow = ctx.createRadialGradient(screenX, cy, 2, screenX, cy, glowSize);
      lampGlow.addColorStop(0,   `rgba(255, 220, 120, ${0.55 * flicker})`);
      lampGlow.addColorStop(0.3, `rgba(255, 180, 80,  ${0.3  * flicker})`);
      lampGlow.addColorStop(0.7, `rgba(240, 140, 40,  ${0.1  * flicker})`);
      lampGlow.addColorStop(1,   "rgba(200, 100, 0, 0)");
      ctx.fillStyle = lampGlow;
      ctx.beginPath();
      ctx.ellipse(screenX, cy, glowSize, glowSize * 0.55, 0, 0, Math.PI * 2);
      ctx.fill();

      // Warm floor pool beneath the lamp
      const floorGlow = ctx.createRadialGradient(screenX, screenY, 2, screenX, screenY, 28);
      floorGlow.addColorStop(0, `rgba(255, 210, 100, ${0.28 * flicker})`);
      floorGlow.addColorStop(1, "rgba(255, 160, 40, 0)");
      ctx.fillStyle = floorGlow;
      ctx.beginPath();
      ctx.ellipse(screenX, screenY, 28, 14, 0, 0, Math.PI * 2);
      ctx.fill();

      ctx.globalCompositeOperation = "source-over";
      ctx.restore();
    }

    // ── Potted fern — leaves swaying in gentle wind ───────────────────────
    if (type === "potted_fern" || type === "fern") {
      const windPhase = t * 0.8;
      const leafColors = ["#2d5a27", "#3a7a32", "#4a9a40", "#5ab450", "#2a4820"];

      ctx.save();

      for (let i = 0; i < 9; i++) {
        const baseAngle = ((i / 9) * Math.PI * 2) - Math.PI / 2;
        const sway      = Math.sin(windPhase + i * 0.7) * 2.5;
        const length    = 12 + (i % 3) * 5;
        const lx        = screenX + Math.cos(baseAngle + sway * 0.06) * length;
        const ly        = screenY - 28 + Math.sin(baseAngle + sway * 0.06) * length * 0.55;

        ctx.strokeStyle = leafColors[i % leafColors.length];
        ctx.lineWidth   = 1.5;
        ctx.globalAlpha = 0.45 + Math.sin(windPhase + i * 1.1) * 0.15;

        ctx.beginPath();
        ctx.moveTo(screenX, screenY - 24);
        ctx.quadraticCurveTo(
          screenX + Math.cos(baseAngle) * length * 0.5 + sway,
          screenY - 28 + Math.sin(baseAngle) * length * 0.3,
          lx, ly
        );
        ctx.stroke();

        ctx.fillStyle   = leafColors[(i + 2) % leafColors.length];
        ctx.globalAlpha = 0.5;
        ctx.beginPath();
        ctx.arc(lx, ly, 2, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalAlpha = 1;
      ctx.restore();
    }

    // ── Fireplace — animated flames, dancing light, embers, smoke ──────
    if (type === "fireplace") {
      if (placement.state["lit"] === false) return;

      const flicker = 0.8 + Math.sin(t * 17) * 0.12 + Math.sin(t * 31) * 0.07;
      const fireBaseY = screenY - 6;
      const fireBaseX = screenX;

      ctx.save();

      // ── 1. Warm ambient glow (screen composite) ─────────────────
      ctx.globalCompositeOperation = "screen";

      const fireGlow = ctx.createRadialGradient(
        fireBaseX, fireBaseY - 12, 4,
        fireBaseX, fireBaseY, 70
      );
      fireGlow.addColorStop(0,   `rgba(255, 160, 40,  ${0.75 * flicker})`);
      fireGlow.addColorStop(0.3, `rgba(255, 100, 20,  ${0.45 * flicker})`);
      fireGlow.addColorStop(0.6, `rgba(220, 60,  10,  ${0.2  * flicker})`);
      fireGlow.addColorStop(1,   "rgba(160, 30, 0, 0)");
      ctx.fillStyle = fireGlow;
      ctx.beginPath();
      ctx.ellipse(fireBaseX, fireBaseY, 70, 50, 0, 0, Math.PI * 2);
      ctx.fill();

      // ── 2. Ember bed glow (hot coals at base) ──────────────────
      const bedGlow = ctx.createRadialGradient(fireBaseX, fireBaseY, 1, fireBaseX, fireBaseY, 14);
      bedGlow.addColorStop(0, `rgba(255, 80, 10, ${0.65 * flicker})`);
      bedGlow.addColorStop(0.5, `rgba(200, 40, 5, ${0.35 * flicker})`);
      bedGlow.addColorStop(1, "rgba(120, 20, 0, 0)");
      ctx.fillStyle = bedGlow;
      ctx.beginPath();
      ctx.ellipse(fireBaseX, fireBaseY + 2, 14, 6, 0, 0, Math.PI * 2);
      ctx.fill();

      // ── 3. Animated flame tongues (5 bezier flames) ────────────
      ctx.globalCompositeOperation = "lighter";
      const flameData = [
        { ox: 0,  h: 20, w: 5,  speed: 3.7,  phase: 0    },  // center tall
        { ox: -5, h: 15, w: 4,  speed: 4.1,  phase: 1.2  },  // left mid
        { ox: 5,  h: 14, w: 3.5,speed: 4.5,  phase: 2.4  },  // right mid
        { ox: -3, h: 11, w: 3,  speed: 5.2,  phase: 3.6  },  // inner left
        { ox: 4,  h: 12, w: 3,  speed: 5.8,  phase: 4.8  },  // inner right
      ];

      for (const f of flameData) {
        const sway = Math.sin(t * f.speed + f.phase) * 3;
        const heightVar = 1 + Math.sin(t * f.speed * 0.7 + f.phase * 0.5) * 0.2;
        const fh = f.h * heightVar * flicker;
        const fx = fireBaseX + f.ox + sway * 0.5;
        const fy = fireBaseY;

        // Outer flame (orange-red)
        const outerGrad = ctx.createLinearGradient(fx, fy, fx + sway, fy - fh);
        outerGrad.addColorStop(0, `rgba(255, 100, 20, ${0.7 * flicker})`);
        outerGrad.addColorStop(0.4, `rgba(255, 160, 40, ${0.5 * flicker})`);
        outerGrad.addColorStop(1, "rgba(255, 200, 60, 0)");
        ctx.fillStyle = outerGrad;
        ctx.beginPath();
        ctx.moveTo(fx - f.w, fy);
        ctx.quadraticCurveTo(fx - f.w * 0.3 + sway * 0.3, fy - fh * 0.6, fx + sway, fy - fh);
        ctx.quadraticCurveTo(fx + f.w * 0.3 + sway * 0.7, fy - fh * 0.6, fx + f.w, fy);
        ctx.closePath();
        ctx.fill();

        // Inner flame (yellow-white hot core, smaller)
        const innerH = fh * 0.6;
        const innerW = f.w * 0.5;
        const innerGrad = ctx.createLinearGradient(fx, fy, fx + sway * 0.5, fy - innerH);
        innerGrad.addColorStop(0, `rgba(255, 240, 180, ${0.6 * flicker})`);
        innerGrad.addColorStop(0.5, `rgba(255, 255, 220, ${0.4 * flicker})`);
        innerGrad.addColorStop(1, "rgba(255, 255, 255, 0)");
        ctx.fillStyle = innerGrad;
        ctx.beginPath();
        ctx.moveTo(fx - innerW, fy);
        ctx.quadraticCurveTo(fx + sway * 0.2, fy - innerH * 0.7, fx + sway * 0.5, fy - innerH);
        ctx.quadraticCurveTo(fx + sway * 0.3, fy - innerH * 0.7, fx + innerW, fy);
        ctx.closePath();
        ctx.fill();
      }

      // ── 4. Amber scatter on floor ──────────────────────────────
      ctx.globalCompositeOperation = "screen";
      for (let di = -1; di <= 1; di++) {
        const fx    = fireBaseX + di * 32;
        const fy    = fireBaseY + 8 + Math.abs(di) * 8;
        const fGlow = ctx.createRadialGradient(fx, fy, 2, fx, fy, 24);
        fGlow.addColorStop(0, `rgba(255, 140, 40, ${0.3 * flicker})`);
        fGlow.addColorStop(1, "rgba(200, 80, 0, 0)");
        ctx.fillStyle = fGlow;
        ctx.beginPath();
        ctx.ellipse(fx, fy, 24, 12, 0, 0, Math.PI * 2);
        ctx.fill();
      }

      // ── 5. Rising ember particles ──────────────────────────────
      ctx.globalCompositeOperation = "lighter";
      for (let i = 0; i < 8; i++) {
        const phase = (t * 1.2 + i * 0.35) % 1;
        const drift = Math.sin(t * 2.5 + i * 2.1) * (6 + phase * 4);
        const ex    = fireBaseX + drift;
        const ey    = fireBaseY - 4 - phase * 36;
        const ea    = (1 - phase) * 0.8 * flicker;
        const er    = (1.4 - phase * 0.8);

        ctx.globalAlpha = ea;
        ctx.fillStyle = i % 3 === 0 ? "#ffdd60" : i % 3 === 1 ? "#ff9020" : "#ff6010";
        ctx.beginPath();
        ctx.arc(ex, ey, er, 0, Math.PI * 2);
        ctx.fill();
      }

      // ── 6. Smoke wisps (subtle, above flames) ─────────────────
      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = 1;
      for (let i = 0; i < 3; i++) {
        const smokePhase = (t * 0.4 + i * 0.6) % 1;
        const smokeX = fireBaseX + Math.sin(t * 0.8 + i * 2.0) * 8;
        const smokeY = fireBaseY - 26 - smokePhase * 30;
        const smokeA = Math.max(0, (1 - smokePhase) * 0.08 - smokePhase * 0.03);
        const smokeR = 3 + smokePhase * 6;

        if (smokeA > 0.005) {
          ctx.globalAlpha = smokeA;
          ctx.fillStyle = "#8a8a9a";
          ctx.beginPath();
          ctx.arc(smokeX, smokeY, smokeR, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "source-over";
      ctx.restore();
    }
  }

  /**
   * Apply the mood tint as a color wash using multiply compositing.
   * This is what makes evening rooms golden and night rooms indigo-blue.
   */
  private drawMoodOverlay(mood: MoodLighting): void {
    const { ctx } = this;
    ctx.save();
    ctx.globalCompositeOperation = "multiply";
    ctx.fillStyle = mood.tint;
    ctx.fillRect(0, 0, this.logW, this.logH);
    ctx.restore();
  }

  /** Soft radial vignette around the canvas edges — depth without drama. */
  private drawVignette(): void {
    const { ctx } = this;
    const cx = this.logW / 2;
    const cy = this.logH / 2;
    const r  = Math.sqrt(this.logW * this.logW + this.logH * this.logH) * 0.55;

    const grad = ctx.createRadialGradient(cx, cy, r * 0.55, cx, cy, r);
    grad.addColorStop(0, "rgba(0,0,0,0)");
    grad.addColorStop(0.6, "rgba(0,0,0,0.15)");
    grad.addColorStop(1, "rgba(0,0,0,0.5)");

    ctx.save();
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, this.logW, this.logH);
    ctx.restore();
  }

  /** Subtle red edge glow for combat stance — danger at the periphery. */
  private _combatVignetteW = 0;
  private _combatVignetteH = 0;
  private _combatVignetteGrads: { top: CanvasGradient; bot: CanvasGradient; left: CanvasGradient; right: CanvasGradient } | null = null;

  private drawCombatVignette(timestamp: number): void {
    const { ctx } = this;
    const w = this.logW, h = this.logH;
    const pulse = 0.08 + Math.sin(timestamp * 0.0015) * 0.03;

    // Cache gradient geometry — only recreate when canvas resizes
    if (!this._combatVignetteGrads || this._combatVignetteW !== w || this._combatVignetteH !== h) {
      this._combatVignetteW = w;
      this._combatVignetteH = h;
      const top = ctx.createLinearGradient(0, 0, 0, h * 0.08);
      top.addColorStop(0, "rgba(180, 30, 30, 1)");
      top.addColorStop(1, "rgba(180, 30, 30, 0)");
      const bot = ctx.createLinearGradient(0, h, 0, h * 0.92);
      bot.addColorStop(0, "rgba(180, 30, 30, 1)");
      bot.addColorStop(1, "rgba(180, 30, 30, 0)");
      const left = ctx.createLinearGradient(0, 0, w * 0.06, 0);
      left.addColorStop(0, "rgba(180, 30, 30, 0.7)");
      left.addColorStop(1, "rgba(180, 30, 30, 0)");
      const right = ctx.createLinearGradient(w, 0, w * 0.94, 0);
      right.addColorStop(0, "rgba(180, 30, 30, 0.7)");
      right.addColorStop(1, "rgba(180, 30, 30, 0)");
      this._combatVignetteGrads = { top, bot, left, right };
    }

    const g = this._combatVignetteGrads;
    ctx.save();
    ctx.globalAlpha = pulse;
    ctx.fillStyle = g.top;
    ctx.fillRect(0, 0, w, h * 0.08);
    ctx.fillStyle = g.bot;
    ctx.fillRect(0, h * 0.92, w, h * 0.08);
    ctx.fillStyle = g.left;
    ctx.fillRect(0, 0, w * 0.06, h);
    ctx.fillStyle = g.right;
    ctx.fillRect(w * 0.94, 0, w * 0.06, h);
    ctx.restore();
  }

  // ─── Dust Motes ───────────────────────────────────────────────────────────

  /**
   * Age existing motes and spawn replacements.
   *
   * Motes concentrate near lamp and fireplace positions so they appear
   * to be lit. They drift with slow Brownian motion and a gentle upward bias
   * (warm air rising). Each mote lives for ~20 seconds.
   */
  private updateDustMotes(
    dt: number,
    room: import("../engine/state.js").Room,
    camera: Camera
  ): void {
    // Collect lamp anchor points in screen space
    const anchors: ScreenPoint[] = [];
    for (const f of room.furniture) {
      const isLamp      = f.type === "floor_lamp" || f.type === "lamp";
      const isFireplace = f.type === "fireplace";
      if ((isLamp || isFireplace) &&
          f.state["on"] !== false &&
          f.state["lit"] !== false) {
        const snapped = furnitureRenderPosition(f, room.walls);
        anchors.push(applyCamera(
          worldToScreen(snapped.wx, snapped.wy, 0.3, true),
          camera
        ));
      }
    }

    if (anchors.length === 0) {
      anchors.push({ x: this.logW / 2, y: this.logH / 2 });
    }

    // Age and move existing motes
    for (const mote of this.dustMotes) {
      mote.life -= dt * 0.05; // ~20s lifespan

      // Brownian nudge with upward bias
      mote.vx += (Math.random() - 0.5) * 4 * dt;
      mote.vy += (Math.random() - 0.51) * 4 * dt;

      // Speed cap
      const spd = Math.sqrt(mote.vx * mote.vx + mote.vy * mote.vy);
      if (spd > mote.speed) {
        mote.vx = (mote.vx / spd) * mote.speed;
        mote.vy = (mote.vy / spd) * mote.speed;
      }

      mote.x += mote.vx * dt;
      mote.y += mote.vy * dt;

      // Envelope: fade in first 10%, hold, fade out last 15%
      if (mote.life > 0.9) {
        mote.alpha = (1 - mote.life) * 10;
      } else if (mote.life < 0.15) {
        mote.alpha = mote.life / 0.15;
      } else {
        mote.alpha = 1;
      }
    }

    this.dustMotes = this.dustMotes.filter((m) => m.life > 0);

    // Spawn to maintain population
    const deficit = DUST_MOTE_COUNT - this.dustMotes.length;
    for (let i = 0; i < deficit; i++) {
      const anchor = anchors[Math.floor(Math.random() * anchors.length)];
      const angle  = Math.random() * Math.PI * 2;
      const radius = 20 + Math.random() * 60;

      this.dustMotes.push({
        x:     anchor.x + Math.cos(angle) * radius,
        y:     anchor.y + Math.sin(angle) * radius,
        vx:    (Math.random() - 0.5) * 8,
        vy:    (Math.random() - 0.6) * 8,
        life:  0.95 + Math.random() * 0.05,
        alpha: 0,
        size:  0.8 + Math.random() * 1.4,
        speed: 5 + Math.random() * 8,
      });
    }
  }

  /**
   * Draw footstep dust particles — barely-there micro-particles that
   * you notice subconsciously. Tile-type-dependent colors and densities.
   */
  private drawFootstepParticles(state: GameState): void {
    const { ctx } = this;
    const particles = state.footstepParticles;
    if (particles.length === 0) return;

    ctx.save();

    for (const p of particles) {
      const t = p.life / p.maxLife;  // 1 = just born, 0 = about to die

      // Smooth fade: full alpha in the first 30%, then linear fade
      const fadeAlpha = t > 0.7 ? 1 : t / 0.7;
      const drawAlpha = p.alpha * fadeAlpha;

      if (drawAlpha < 0.005) continue;

      ctx.globalAlpha = drawAlpha;
      const pr = Math.max(0, Math.min(255, p.r)) | 0;
      const pg = Math.max(0, Math.min(255, p.g)) | 0;
      const pb = Math.max(0, Math.min(255, p.b)) | 0;
      ctx.fillStyle = `rgb(${pr},${pg},${pb})`;

      if (p.size <= 1.5) {
        // Tiny particles: fill rect is faster than arc
        ctx.fillRect(p.x - p.size * 0.5, p.y - p.size * 0.5, p.size, p.size);
      } else {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.globalAlpha = 1;
    ctx.restore();
  }

  /**
   * Draw arena ambient particles — sand wisps drift lazily, torch sparks
   * arc upward with gravity, dust motes float in the light.
   */
  private drawArenaParticles(state: GameState): void {
    const { ctx } = this;
    const particles = state.arenaParticles;
    if (particles.length === 0) return;

    ctx.save();

    for (const p of particles) {
      const t = p.life / p.maxLife; // 1 = fresh, 0 = dying
      const fadeAlpha = t > 0.7 ? 1 : t > 0.2 ? (t - 0.2) / 0.5 : t / 0.2;

      if (fadeAlpha < 0.01) continue;

      ctx.globalAlpha = fadeAlpha * 0.7;

      if (p.type === "spark") {
        // Torch sparks: bright warm glow with slight bloom
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 2);
        grad.addColorStop(0, p.color);
        grad.addColorStop(1, "rgba(255, 160, 48, 0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 2, 0, Math.PI * 2);
        ctx.fill();
        // Bright core
        ctx.fillStyle = "#fff8e0";
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 0.5, 0, Math.PI * 2);
        ctx.fill();
      } else {
        // Sand wisps and dust: simple circles
        ctx.fillStyle = p.color;
        if (p.size <= 1.2) {
          ctx.fillRect(p.x - p.size * 0.5, p.y - p.size * 0.5, p.size, p.size);
        } else {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    ctx.globalAlpha = 1;
    ctx.restore();
  }

  /** Draw dust motes as tiny warm circles with additive screen blending. */
  private drawDustMotes(timestamp: number): void {
    const { ctx } = this;
    const t = timestamp / 1000;

    ctx.save();
    ctx.globalCompositeOperation = "screen";

    for (const mote of this.dustMotes) {
      const twinkle = 0.7 + Math.sin(t * 3 + mote.x * 0.1) * 0.3;
      const a       = mote.alpha * twinkle * 0.45;
      if (a < 0.01) continue;

      ctx.globalAlpha = a;
      // Vary dust mote color: 70% warm gold, 20% cool scatter, 10% deep amber
      const moteHash = (mote.x * 73 + mote.y * 37) | 0;
      const moteType = moteHash % 10;
      ctx.fillStyle = moteType < 7 ? "#ffe0a0" : moteType < 9 ? "#c0d8ff" : "#ffc060";
      ctx.beginPath();
      ctx.arc(mote.x, mote.y, mote.size, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
    ctx.restore();
  }

  // ─── Chat Bubbles ─────────────────────────────────────────────────────────

  /**
   * Render active chat bubbles above the player's actual head in world space.
   *
   * Head position is derived from elevation 1.8 — the isometric projection
   * lifts this to the correct screen height automatically. This means the
   * bubble tracks the character's real sprite position rather than a hardcoded
   * pixel offset from the foot point.
   */
  private drawChatBubbles(state: GameState, camera: Camera): void {
    const { ctx, logW } = this;
    const bubbles = state.chat.activeBubbles;
    if (bubbles.length === 0) return;

    const p = state.player;
    let playerTileX = p.position.tileX;
    let playerTileY = p.position.tileY;

    const bubbleNext = p.movementQueue?.[0];
    if (p.moveProgress > 0 && bubbleNext) {
      playerTileX += (bubbleNext.tileX - p.position.tileX) * p.moveProgress;
      playerTileY += (bubbleNext.tileY - p.position.tileY) * p.moveProgress;
    }

    const px = playerTileX + 0.5;
    const py = playerTileY + 0.5;

    // Compute bubble anchor from the character's actual head position:
    // Feet screen pos → subtract (CHAR_H-8)*0.5 = 68px (head top at 0.5 scale) × zoom
    const feetScreen = applyCamera(worldToScreen(px, py, p.position.elevation), camera);
    const headY      = Math.round(feetScreen.y - 68 * camera.zoom) - 4;
    const headScreen = { x: feetScreen.x, y: headY };

    ctx.save();
    ctx.font         = BUBBLE_FONT;
    ctx.textBaseline = "top";
    ctx.textAlign    = "center";

    let stackOffsetY = 0;

    for (const bubble of bubbles) {
      const msRemaining = bubble.until - Date.now();
      if (msRemaining <= 0) continue;

      // Determine anchor position — player or NPC
      let anchorScreen = headScreen;
      let anchorHeadY = headY;
      if (bubble.senderId === state.player.id) {
        // Player bubble — already set
      } else if (bubble.senderId.startsWith("npc_")) {
        // NPC bubble — find the NPC
        const npcId = bubble.senderId.replace("npc_", "");
        const npcData = state.npcs.find(n => n.id === npcId);
        if (!npcData) continue;
        const npcFeet = applyCamera(worldToScreen(npcData.position.tileX + 0.5, npcData.position.tileY + 0.5, npcData.position.elevation), camera);
        // NPCs render at ctx.scale(0.5) — head offset is half of player's
        anchorHeadY = Math.round(npcFeet.y - 38 * camera.zoom) - 4;
        anchorScreen = { x: npcFeet.x, y: anchorHeadY };
      } else {
        continue; // Unknown sender
      }

      const alpha  = msRemaining < 800 ? msRemaining / 800 : 1;
      const lines  = wrapText(bubble.text, BUBBLE_MAX_CHARS);
      const lineH  = 16;
      const padX   = 12;
      const padY   = 8;
      const bH     = lines.length * lineH + padY * 2;

      // Measure each line individually so the bubble fits the widest line exactly
      const lineWidths = lines.map((l) => ctx.measureText(l).width);
      const maxTextW   = Math.max(...lineWidths);
      const bW         = Math.ceil(maxTextW + padX * 2);

      // Center on head, then clamp to screen edges with a 4px margin
      let bx = Math.round(anchorScreen.x - bW / 2);
      bx     = Math.max(4, Math.min(bx, logW - bW - 4));
      const by = Math.round(anchorHeadY - bH - stackOffsetY);

      // Tail anchor stays at head center regardless of bubble clamping
      const tailX    = Math.round(anchorScreen.x);
      const tailBase = by + bH;

      ctx.globalAlpha = alpha;

      // ── Bubble shadow ───────────────────────────────────────────────
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,0.25)";
      ctx.shadowBlur = 6;
      ctx.shadowOffsetY = 2;
      ctx.fillStyle = "#f5ebd8";
      roundRect(ctx, bx, by, bW, bH, 8);
      ctx.fill();
      ctx.restore();

      // ── Bubble body ─────────────────────────────────────────────────
      const bubbleGrad = ctx.createLinearGradient(bx, by, bx, by + bH);
      bubbleGrad.addColorStop(0, "#f8f0e0");
      bubbleGrad.addColorStop(1, "#f0e4d0");
      ctx.fillStyle   = bubbleGrad;
      ctx.strokeStyle = "#d4a87a";
      ctx.lineWidth   = 1.5;
      roundRect(ctx, bx, by, bW, bH, 8);
      ctx.fill();
      ctx.stroke();

      // ── Tail triangle ───────────────────────────────────────────────
      ctx.fillStyle = "#f0e4d0";
      ctx.lineJoin  = "round";
      ctx.beginPath();
      ctx.moveTo(tailX - 6, tailBase);
      ctx.lineTo(tailX,     tailBase + 8);
      ctx.lineTo(tailX + 6, tailBase);
      ctx.closePath();
      ctx.fill();

      // Cover the bubble bottom stroke where the tail meets it
      ctx.fillRect(tailX - 5, tailBase - 1, 10, 2);

      // Stroke only the two visible tail sides (not the shared top edge)
      ctx.strokeStyle = "#d4a87a";
      ctx.lineWidth   = 1.5;
      ctx.lineJoin    = "round";
      ctx.beginPath();
      ctx.moveTo(tailX - 6, tailBase);
      ctx.lineTo(tailX,     tailBase + 8);
      ctx.lineTo(tailX + 6, tailBase);
      ctx.stroke();

      // ── Text — centered per line ────────────────────────────────────
      ctx.save();
      roundRect(ctx, bx, by, bW, bH, 8);
      ctx.clip();

      ctx.fillStyle = "#4a3a2a";
      const centerX = bx + bW / 2;
      for (let i = 0; i < lines.length; i++) {
        ctx.fillText(lines[i], centerX, by + padY + i * lineH);
      }
      ctx.restore();

      stackOffsetY += bH + 14;
    }

    ctx.globalAlpha = 1;
    ctx.restore();
  }

  // ─── Furniture Highlights, Selection & Placement Ghost ────────────────────

  /**
   * All furniture visual feedback in one pass:
   *  - Hover glow: subtle white outline around furniture under cursor
   *  - Selection glow: golden pulsing highlight around selected furniture
   *  - Placement ghost: semi-transparent preview of furniture being placed/moved
   *  - Action toolbar: floating buttons above selected furniture
   */
  private drawFurnitureHighlights(
    timestamp: number,
    state: GameState,
    room: import("../engine/state.js").Room,
    camera: Camera,
    grid: TileGrid
  ): void {
    const { ctx } = this;
    const hovered = state.ui.hoveredTile;
    const placing = state.ui.placingFurnitureType;
    const moving  = state.ui.movingFurnitureId;

    // ── 1. Update hoveredFurnitureId each frame ──
    // Check all footprint tiles for multi-tile furniture (not just anchor tile)
    state.ui.hoveredFurnitureId = null;
    if (hovered && !placing) {
      const fOnTile = room.furniture.find(f => {
        if (f.id === moving) return false;
        const footprint = getFootprintTiles(f);
        return footprint.some(t => t.x === hovered.x && t.y === hovered.y);
      });
      if (fOnTile) state.ui.hoveredFurnitureId = fOnTile.id;
    }

    // ── 2. Hover tile highlight ──
    // (Now handled in drawTileHoverHighlight, rendered on the floor before entities.
    //  The old highlightTile call is removed — the new one has warm glow, color
    //  variants for creatures/NPCs/blocked tiles, and a subtle breathing pulse.)

    // ── 3. Hover glow on furniture under cursor ──
    // Edit mode: white shimmer. Normal mode: subtle warm glow for interactable feedback.
    if (state.ui.hoveredFurnitureId && state.ui.hoveredFurnitureId !== state.ui.selectedFurniture) {
      const hf = room.furniture.find(f => f.id === state.ui.hoveredFurnitureId);
      if (hf) {
        this.drawFurnitureGlow(hf, camera, timestamp, state.ui.editMode ? "hover" : "interact");
        // Highlight all footprint tiles for multi-tile furniture
        if (state.ui.editMode) {
          const hoverFoot = getFootprintTiles(hf);
          for (const t of hoverFoot) {
            highlightTile(ctx, t.x, t.y, grid, camera, "rgba(255, 220, 140, 0.18)");
          }
        }
      }
    }

    // ── 4. Selection glow + toolbar (edit mode only) ──
    if (state.ui.editMode && state.ui.selectedFurniture && !placing) {
      const sf = room.furniture.find(f => f.id === state.ui.selectedFurniture);
      if (sf) {
        this.drawFurnitureGlow(sf, camera, timestamp, "selected");
        // Highlight all footprint tiles for selected furniture
        const selFoot = getFootprintTiles(sf);
        for (const t of selFoot) {
          highlightTile(ctx, t.x, t.y, grid, camera, "rgba(200, 160, 60, 0.25)");
        }
        this.drawFurnitureActionToolbar(sf, camera, state, timestamp);
      }
    }

    // ── 5. Moving furniture origin indicator — dashed outline where it came from ──
    if (moving && state.ui.movingOriginalTile) {
      const orig = state.ui.movingOriginalTile;
      const origScreen = applyCamera(worldToScreen(orig.tileX + 0.5, orig.tileY + 0.5, 0, true), camera);
      const hw = TILE_W_HALF * camera.zoom;
      const hh = TILE_H_HALF * camera.zoom;
      const { ctx } = this;
      ctx.save();
      const originPulse = 0.15 + Math.sin(timestamp * 0.003) * 0.08;
      ctx.globalAlpha = originPulse;
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = "rgba(200, 160, 80, 0.8)";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(origScreen.x, origScreen.y - hh);
      ctx.lineTo(origScreen.x + hw, origScreen.y);
      ctx.lineTo(origScreen.x, origScreen.y + hh);
      ctx.lineTo(origScreen.x - hw, origScreen.y);
      ctx.closePath();
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }

    // ── 6. Placement ghost preview ──
    if (placing && hovered) {
      this.drawPlacementGhost(timestamp, state, hovered, camera, grid);
    }
  }

  /**
   * Draw a glowing highlight around a furniture piece.
   * "hover" = subtle white shimmer. "selected" = golden pulsing glow.
   */
  private drawFurnitureGlow(
    placement: FurniturePlacement,
    camera: Camera,
    timestamp: number,
    mode: "hover" | "selected" | "interact"
  ): void {
    const { ctx } = this;
    const snapped = furnitureRenderPosition(placement, this.lastRoom?.walls ?? []);
    const screen  = applyCamera(worldToScreen(snapped.wx, snapped.wy, placement.elevation, true), camera);

    const registryId = toRegistryId(placement.type);
    const ft = getFurnitureType(registryId);
    if (!ft) return;

    const sw = ft.sprite.width;
    const sh = ft.sprite.height;

    ctx.save();
    ctx.translate(screen.x, screen.y);
    ctx.scale(camera.zoom, camera.zoom);

    if (mode === "selected") {
      // Golden pulsing glow
      const pulse = 0.3 + Math.sin(timestamp * 0.004) * 0.15;
      // Outer glow
      ctx.shadowColor = `rgba(212, 160, 48, ${pulse})`;
      ctx.shadowBlur = 12;
      ctx.strokeStyle = `rgba(212, 160, 48, ${pulse + 0.2})`;
      ctx.lineWidth = 2;
      this.strokeFurnitureBounds(0, 0, sw, sh);
      // Inner glow pass
      ctx.shadowBlur = 6;
      ctx.strokeStyle = `rgba(240, 200, 96, ${pulse * 0.7})`;
      ctx.lineWidth = 1;
      this.strokeFurnitureBounds(0, 0, sw, sh);
    } else if (mode === "hover") {
      // Build-mode hover — white shimmer
      ctx.shadowColor = "rgba(255, 255, 255, 0.25)";
      ctx.shadowBlur = 8;
      ctx.strokeStyle = "rgba(255, 255, 255, 0.3)";
      ctx.lineWidth = 1.5;
      this.strokeFurnitureBounds(0, 0, sw, sh);
    } else {
      // Normal-mode interact glow — subtle warm amber pulse
      const pulse = 0.12 + Math.sin(timestamp * 0.003) * 0.06;
      ctx.shadowColor = `rgba(200, 160, 80, ${pulse})`;
      ctx.shadowBlur = 6;
      ctx.strokeStyle = `rgba(200, 160, 80, ${pulse + 0.08})`;
      ctx.lineWidth = 1;
      this.strokeFurnitureBounds(0, 0, sw, sh);
    }

    // Pipeline-generated badge — small "3D" indicator above hovered pipeline assets
    if (ft.pipelineGenerated) {
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      const badgeY = -sh * 0.7;
      // Badge background pill
      const badgeW = 26, badgeH = 14, badgeR = 4;
      ctx.fillStyle = "rgba(60, 180, 220, 0.85)";
      roundRect(ctx, -badgeW / 2, badgeY - badgeH / 2, badgeW, badgeH, badgeR);
      ctx.fill();
      // "3D" text
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 9px monospace";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("3D", 0, badgeY);
    }

    ctx.restore();
  }

  /**
   * Stroke a diamond-ish bounding region around a furniture sprite anchor.
   * Approximated as a rounded rect centered on the sprite's visual area.
   */
  private strokeFurnitureBounds(
    cx: number, cy: number,
    spriteW: number, spriteH: number
  ): void {
    const { ctx } = this;
    const hw = spriteW * 0.5;
    const hh = spriteH * 0.6;
    const x = cx - hw;
    const y = cy - hh;
    roundRect(ctx, x, y, spriteW, hh + spriteH * 0.15, 6);
    ctx.stroke();
  }

  /**
   * Semi-transparent ghost of the furniture being placed, rendered at the hovered tile.
   * Shows the actual furniture sprite so the user can see exactly what will land.
   */
  private drawPlacementGhost(
    timestamp: number,
    state: GameState,
    hovered: { x: number; y: number },
    camera: Camera,
    grid: TileGrid
  ): void {
    const { ctx } = this;
    const typeId = state.ui.placingFurnitureType!;
    const registryId = toRegistryId(typeId);
    const ft = getFurnitureType(registryId);
    const room = state.currentRoom;
    if (!room) return;

    // Compute the full footprint for the placement ghost
    const tempFootprint = getFootprintTiles({
      type: typeId,
      tileX: hovered.x,
      tileY: hovered.y,
      direction: state.ui.placingDirection,
    });

    // Check each tile in the footprint for validity
    let anyInvalid = false;
    for (const t of tempFootprint) {
      const ftile = room.tiles[t.y]?.[t.x];
      const oob = t.x < 0 || t.x >= room.width || t.y < 0 || t.y >= room.height;
      const occupied = !!ftile?.furniture;
      if (oob || !ftile || occupied) anyInvalid = true;
    }

    // Highlight all footprint tiles — green = valid, red = invalid
    for (const t of tempFootprint) {
      const color = anyInvalid ? "rgba(180, 40, 40, 0.35)" : "rgba(40, 180, 80, 0.30)";
      highlightTile(ctx, t.x, t.y, grid, camera, color);
    }

    const tile = room.tiles[hovered.y]?.[hovered.x];

    // Ghost sprite
    if (ft) {
      // Build a temporary placement to compute wall-snapped position
      const tempPlacement: FurniturePlacement = {
        id: "__ghost__",
        type: typeId,
        tileX: hovered.x,
        tileY: hovered.y,
        elevation: tile?.elevation ?? 0,
        direction: state.ui.placingDirection,
        state: {},
      };
      const snapped = furnitureRenderPosition(tempPlacement, room.walls);
      const screen = applyCamera(worldToScreen(snapped.wx, snapped.wy, tempPlacement.elevation, true), camera);
      const rotation = getVisualRotation(typeId, state.ui.placingDirection);

      ctx.save();
      ctx.translate(screen.x, screen.y);
      ctx.scale(camera.zoom, camera.zoom);
      ctx.globalAlpha = anyInvalid ? 0.25 : 0.55;
      renderFurniture(ctx, ft, 0, 0, rotation, {}, timestamp);
      ctx.globalAlpha = 1;
      ctx.restore();
    }
  }

  /**
   * Floating action toolbar above the selected furniture piece.
   * Horizontal bar with icon buttons: Rotate, Move, Pick Up.
   */
  private drawFurnitureActionToolbar(
    placement: FurniturePlacement,
    camera: Camera,
    state: GameState,
    timestamp: number
  ): void {
    const { ctx } = this;
    const snapped = furnitureRenderPosition(placement, this.lastRoom?.walls ?? []);
    const screen  = applyCamera(worldToScreen(snapped.wx, snapped.wy, placement.elevation, true), camera);

    const ft = getFurnitureType(toRegistryId(placement.type));
    const spriteH = ft?.sprite.height ?? 48;

    // Position toolbar above the furniture
    const toolbarW = 156;
    const toolbarH = 38;
    const toolbarX = Math.round(screen.x - toolbarW / 2);
    const toolbarY = Math.round(screen.y - spriteH * 0.55 * camera.zoom - toolbarH - 8);

    // Clamp to screen
    const clampedX = Math.max(6, Math.min(toolbarX, this.logW - toolbarW - 6));
    const clampedY = Math.max(6, toolbarY);

    // Entrance animation — slight scale-up from furniture
    const age = timestamp - (this._toolbarShowTime ?? timestamp);
    if (!this._toolbarShowTime || this._toolbarTargetId !== placement.id) {
      this._toolbarShowTime = timestamp;
      this._toolbarTargetId = placement.id;
    }
    const animT = Math.min(1, age / 180);
    const ease = 1 - Math.pow(1 - animT, 3); // ease-out cubic
    if (ease < 0.01) return;

    ctx.save();
    ctx.globalAlpha = ease;

    // Panel background
    const grad = ctx.createLinearGradient(clampedX, clampedY, clampedX, clampedY + toolbarH);
    grad.addColorStop(0, "#2a1d0e");
    grad.addColorStop(1, "#1a1208");
    ctx.fillStyle = grad;
    roundRect(ctx, clampedX, clampedY, toolbarW, toolbarH, 8);
    ctx.fill();

    // Gold border
    ctx.strokeStyle = "#9b7318";
    ctx.lineWidth = 1.5;
    roundRect(ctx, clampedX, clampedY, toolbarW, toolbarH, 8);
    ctx.stroke();

    // Inner highlight
    ctx.strokeStyle = "rgba(212, 160, 48, 0.15)";
    ctx.lineWidth = 1;
    roundRect(ctx, clampedX + 2, clampedY + 2, toolbarW - 4, toolbarH - 4, 6);
    ctx.stroke();

    // Pointer arrow (small triangle pointing down toward furniture)
    const arrowX = Math.max(clampedX + 16, Math.min(screen.x, clampedX + toolbarW - 16));
    const arrowY = clampedY + toolbarH;
    ctx.fillStyle = "#1a1208";
    ctx.beginPath();
    ctx.moveTo(arrowX - 6, arrowY);
    ctx.lineTo(arrowX + 6, arrowY);
    ctx.lineTo(arrowX, arrowY + 6);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = "#9b7318";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(arrowX - 6, arrowY);
    ctx.lineTo(arrowX, arrowY + 6);
    ctx.lineTo(arrowX + 6, arrowY);
    ctx.stroke();

    // ── Buttons ──
    const buttons = [
      { icon: "↻", label: "Rotate",  action: "rotate" },
      { icon: "✥", label: "Move",    action: "move"   },
      { icon: "▲", label: "Pick Up", action: "pickup"  },
    ] as const;

    const btnW = 46;
    const btnGap = 6;
    const totalBtnW = buttons.length * btnW + (buttons.length - 1) * btnGap;
    const btnStartX = clampedX + (toolbarW - totalBtnW) / 2;
    const btnY = clampedY + 4;
    const btnH = toolbarH - 8;

    for (let i = 0; i < buttons.length; i++) {
      const btn = buttons[i];
      const bx = btnStartX + i * (btnW + btnGap);

      // Check hover
      const isHovered = this.hud.mouseX >= bx && this.hud.mouseX <= bx + btnW &&
                        this.hud.mouseY >= btnY && this.hud.mouseY <= btnY + btnH;

      // Button background
      ctx.fillStyle = isHovered ? "rgba(212, 160, 48, 0.20)" : "rgba(212, 160, 48, 0.08)";
      roundRect(ctx, bx, btnY, btnW, btnH, 5);
      ctx.fill();

      if (isHovered) {
        ctx.strokeStyle = "rgba(212, 160, 48, 0.4)";
        ctx.lineWidth = 1;
        roundRect(ctx, bx, btnY, btnW, btnH, 5);
        ctx.stroke();
      }

      // Icon
      ctx.font = "bold 14px sans-serif";
      ctx.fillStyle = isHovered ? "#f0d88a" : "#d4a030";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(btn.icon, bx + btnW / 2, btnY + 3);

      // Label
      ctx.font = "bold 8px monospace";
      ctx.fillStyle = isHovered ? "#f0d88a" : "#8a7240";
      ctx.textBaseline = "bottom";
      ctx.fillText(btn.label, bx + btnW / 2, btnY + btnH - 2);

      // Register hit zone (HUD system for click ripples + hitRegions for priority ordering)
      const toolbarAction = this.makeToolbarAction(btn.action, placement.id);
      this.hud.addHitFromRoom(bx, btnY, btnW, btnH, toolbarAction);
      if (this._state) {
        this._state.ui.hitRegions.push({
          id: `${placement.id}:${btn.action}`,
          priority: 200,
          x: bx, y: btnY, w: btnW, h: btnH,
          action: toolbarAction,
        });
      }
    }

    ctx.globalAlpha = 1;
    ctx.restore();
  }

  /** Factory for toolbar button actions. */
  private makeToolbarAction(
    action: "rotate" | "move" | "pickup",
    furnitureId: string
  ): (state: GameState) => void {
    return (s: GameState) => {
      const room = s.currentRoom;
      if (!room) return;
      const p = room.furniture.find(f => f.id === furnitureId);
      s.ui.uiSounds.push("ui_click");

      if (action === "rotate" && p) {
        const oldDir = p.direction;
        // Clear old footprint before rotating
        const oldFootprint = getFootprintTiles(p);
        for (const t of oldFootprint) {
          const tile = room.tiles[t.y]?.[t.x];
          if (tile) { tile.furniture = undefined; tile.walkable = true; }
        }
        // Rotation cycle: maps any direction to the next 90° step.
        // E/SE→0, S/SW→1, W/NW→2, N/NE→3. Advance by 1.
        const ROT_CYCLE: Record<string, import("../engine/state.js").Direction> = {
          E: "S", SE: "SW", S: "W", SW: "NW", W: "N", NW: "NE", N: "E", NE: "SE",
        };
        p.direction = ROT_CYCLE[p.direction] ?? "SE";
        // Validate new footprint — must be in-bounds and not overlap other furniture
        const newFootprint = getFootprintTiles(p);
        let valid = true;
        for (const t of newFootprint) {
          if (t.x < 0 || t.x >= room.width || t.y < 0 || t.y >= room.height) { valid = false; break; }
          const tile = room.tiles[t.y]?.[t.x];
          if (!tile || (tile.furniture && tile.furniture !== p.id)) { valid = false; break; }
        }
        if (!valid) {
          // Rollback — restore old direction and re-mark old footprint
          p.direction = oldDir;
          for (const t of oldFootprint) {
            const tile = room.tiles[t.y]?.[t.x];
            if (tile) { tile.furniture = p.id; tile.walkable = false; }
          }
        } else {
          for (const t of newFootprint) {
            const tile = room.tiles[t.y]?.[t.x];
            if (tile) { tile.furniture = p.id; tile.walkable = false; }
          }
        }
      } else if (action === "move" && p) {
        s.ui.movingFurnitureId    = furnitureId;
        s.ui.movingOriginalTile   = { tileX: p.tileX, tileY: p.tileY };
        s.ui.placingFurnitureType = p.type;
        s.ui.placingDirection     = p.direction as import("../engine/state.js").Direction;
        // Clear all footprint tiles when lifting for move
        const moveFootprint = getFootprintTiles(p);
        for (const t of moveFootprint) {
          const tile = room.tiles[t.y]?.[t.x];
          if (tile) { tile.furniture = undefined; tile.walkable = true; }
        }
        s.ui.selectedFurniture = null;
      } else if (action === "pickup" && p) {
        const idx = room.furniture.findIndex(f => f.id === furnitureId);
        if (idx === -1) return;
        // Clear all footprint tiles when picking up
        const pickupFootprint = getFootprintTiles(p);
        for (const t of pickupFootprint) {
          const tile = room.tiles[t.y]?.[t.x];
          if (tile) { tile.furniture = undefined; tile.walkable = true; }
        }
        room.furniture.splice(idx, 1);
        const existing = s.player.inventory.find(
          it => it.type === "furniture" && it.furnitureTypeId === p.type
        );
        if (existing) {
          existing.quantity += 1;
        } else {
          s.player.inventory.push({
            id:             `inv_pickup_${Date.now()}`,
            type:           "furniture",
            name:           p.type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
            quantity:       1,
            rarity:         "common",
            acquiredAt:     Date.now(),
            furnitureTypeId: p.type,
          });
        }
        s.ui.selectedFurniture = null;
      }
    };
  }

  // Animation state for toolbar entrance
  private _toolbarShowTime: number | null = null;
  private _toolbarTargetId: string | null = null;
  // Store last room reference for wall-snap lookups in glow drawing
  private lastRoom: import("../engine/state.js").Room | null = null;

  // ─── Furniture Tooltip ────────────────────────────────────────────────────

  /**
   * Show a warm label near the hovered tile when it contains furniture.
   *
   * Uses the human-readable name from the furniture registry; falls back to
   * the raw type string for unregistered furniture.
   */
  private drawFurnitureTooltip(
    state: GameState,
    room: import("../engine/state.js").Room,
    camera: Camera
  ): void {
    const hovered = state.ui.hoveredTile;
    if (!hovered) return;

    const found = room.furniture.find(f => {
      const footprint = getFootprintTiles(f);
      return footprint.some(t => t.x === hovered.x && t.y === hovered.y);
    });
    if (!found) return;

    const ft    = getFurnitureType(toRegistryId(found.type));
    const label = ft ? ft.name : found.type;

    const tileScreen = applyCamera(
      worldToScreen(hovered.x + 0.5, hovered.y + 0.5, 0, true),
      camera
    );

    const { ctx } = this;
    ctx.save();
    ctx.font = "bold 11px sans-serif";

    // Word-wrap into lines that fit MAX_TIP_WIDTH
    const MAX_TIP_WIDTH = 180;
    const pad  = 8;
    const lineH = 14;
    const innerMax = MAX_TIP_WIDTH - pad * 2;
    const words = label.split(" ");
    const lines: string[] = [];
    let cur = "";
    for (const word of words) {
      const test = cur ? `${cur} ${word}` : word;
      if (ctx.measureText(test).width > innerMax && cur) {
        lines.push(cur);
        cur = word;
      } else {
        cur = test;
      }
    }
    if (cur) lines.push(cur);

    const maxLineW = Math.max(...lines.map(l => ctx.measureText(l).width));
    const tipW = Math.ceil(maxLineW + pad * 2);
    const tipH = lines.length * lineH + pad * 2;
    let tipX = Math.round(tileScreen.x - tipW / 2);
    let tipY = Math.round(tileScreen.y - tipH - 18 * camera.zoom);

    // Clamp to screen bounds with 8px margin
    const logW = this.logW;
    const logH = this.logH;
    tipX = Math.max(8, Math.min(tipX, logW - tipW - 8));
    tipY = Math.max(8, Math.min(tipY, logH - tipH - 8));

    ctx.globalAlpha = 0.92;
    // Gradient background for depth
    const ftGrad = ctx.createLinearGradient(tipX, tipY, tipX, tipY + tipH);
    ftGrad.addColorStop(0, "#3a2a1a");
    ftGrad.addColorStop(1, "#1a1008");
    ctx.fillStyle = ftGrad;
    roundRect(ctx, tipX, tipY, tipW, tipH, 4);
    ctx.fill();
    ctx.strokeStyle = "#d4a060";
    ctx.lineWidth   = 1.5;
    ctx.stroke();

    ctx.globalAlpha  = 1;
    ctx.fillStyle    = "#f0d090";
    ctx.textAlign    = "center";
    ctx.textBaseline = "top";
    const centerX = tipX + tipW / 2;
    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i], centerX, tipY + pad + i * lineH);
    }

    ctx.restore();

    // Register interact-prompt hit region so the tooltip is mouse-clickable
    if (ft?.interactable && ft.interactionType && this._state) {
      const furnitureId = found.id;
      this._state.ui.hitRegions.push({
        id: `interact:${furnitureId}`,
        priority: 150,
        x: tipX, y: tipY, w: tipW, h: tipH,
        action: (s: GameState) => {
          s.ui.pendingInteractFurnitureId = furnitureId;
          s.ui.uiSounds.push("ui_click");
        },
      });
    }
  }

  // ─── UI Chrome ────────────────────────────────────────────────────────────

  /**
   * Draw a subtle time + room name display in the top-right corner.
   */
  private drawTimeIndicator(state: GameState): void {
    const { ctx } = this;
    const room = state.currentRoom;
    if (!room) return;

    const timeStr = formatGameTime(state.time.gameHour);
    const roomStr = room.name;

    ctx.save();
    ctx.font         = TIME_FONT;
    ctx.textBaseline = "top";
    ctx.textAlign    = "right";

    // Position below the minimap (center=SW-81, radius=65, room name ~y=155)
    const rightEdge = this.logW - 12;
    const topEdge   = 168;

    const timeW = ctx.measureText(timeStr).width;
    ctx.font     = "10px monospace";
    const roomW  = ctx.measureText(roomStr).width;
    ctx.font     = TIME_FONT;

    const pillW = Math.max(timeW, roomW) + 16;
    const pillH = 32;

    const pillX = Math.max(4, rightEdge - pillW);
    ctx.globalAlpha = 0.60;
    const pillGrad = ctx.createLinearGradient(pillX, topEdge - 4, pillX, topEdge - 4 + pillH);
    pillGrad.addColorStop(0, "rgba(20,15,10,0.7)");
    pillGrad.addColorStop(1, "rgba(10,8,5,0.5)");
    ctx.fillStyle = pillGrad;
    roundRect(ctx, pillX, topEdge - 4, pillW, pillH, 5);
    ctx.fill();
    ctx.globalAlpha = 1;

    // Time text with subtle shadow for readability
    ctx.shadowColor = "rgba(0,0,0,0.5)";
    ctx.shadowBlur  = 3;
    ctx.shadowOffsetY = 1;
    ctx.fillStyle = "#e8c89a";
    ctx.font      = TIME_FONT;
    ctx.fillText(timeStr, rightEdge, topEdge);

    ctx.fillStyle = "#a89a7a";
    ctx.font      = "10px monospace";
    ctx.fillText(roomStr, rightEdge, topEdge + 14);

    ctx.restore();
  }

  // ─── Edit Mode Grid Overlay ──────────────────────────────────────────────

  /**
   * Draw a subtle isometric grid over all floor tiles when in build mode.
   * Tile diamonds are outlined with thin translucent lines; furniture-occupied
   * tiles get a slightly different tint so you can see the footprints.
   */
  private drawEditModeGrid(
    state: GameState,
    room: import("../engine/state.js").Room,
    camera: Camera,
    timestamp: number,
  ): void {
    const { ctx } = this;
    const fadeIn = Math.min(1, (performance.now() - (this._editModeStartTime ?? performance.now())) / 250);
    if (!this._editModeStartTime) this._editModeStartTime = performance.now();

    const baseAlpha = 0.18 * fadeIn;
    const pulse = 0.02 * Math.sin(timestamp * 0.002) * fadeIn;

    ctx.save();

    for (let ty = 0; ty < room.height; ty++) {
      for (let tx = 0; tx < room.width; tx++) {
        const tile = room.tiles[ty]?.[tx];
        if (!tile) continue;

        const elev = tile.elevation;
        const screen = applyCamera(worldToScreen(tx + 0.5, ty + 0.5, elev), camera);

        // Iso diamond half-sizes
        const hw = 32 * camera.zoom;
        const hh = 16 * camera.zoom;

        const hasFurniture = !!tile.furniture;

        ctx.beginPath();
        ctx.moveTo(screen.x, screen.y - hh);       // N
        ctx.lineTo(screen.x + hw, screen.y);        // E
        ctx.lineTo(screen.x, screen.y + hh);        // S
        ctx.lineTo(screen.x - hw, screen.y);        // W
        ctx.closePath();

        // Fill: furniture tiles get a blue tint, empty tiles just a subtle grid
        if (hasFurniture) {
          ctx.fillStyle = `rgba(100, 160, 220, ${(baseAlpha + pulse) * 0.8})`;
          ctx.fill();
        }

        // Stroke: thin white lines for the grid
        ctx.strokeStyle = `rgba(255, 255, 255, ${baseAlpha + pulse})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
    }

    ctx.restore();
  }

  private _editModeStartTime: number | null = null;
  private _editModeExitTime: number | null = null;

  // ─── Tile Hover Highlight ────────────────────────────────────────────────

  private drawTileHoverHighlight(
    timestamp: number,
    state: GameState,
    room: import("../engine/state.js").Room,
    camera: Camera,
  ): void {
    const hovered = state.ui.hoveredTile;
    if (!hovered) return;
    if (state.ui.placingFurnitureType) return;
    const { x: hx, y: hy } = hovered;
    if (hx < 0 || hx >= room.width || hy < 0 || hy >= room.height) return;
    const tile = room.tiles[hy]?.[hx];
    if (!tile) return;
    const { ctx } = this;
    const elev = tile.elevation;
    const creatureOnTile = state.creatures.find(c => c.position.tileX === hx && c.position.tileY === hy);
    const npcOnTile = state.npcs.find(n => n.position.tileX === hx && n.position.tileY === hy);
    let fillColor: string;
    let strokeColor: string;
    let glowColor: string;
    if (creatureOnTile) {
      fillColor = "rgba(220, 80, 60, 0.12)";
      strokeColor = "rgba(220, 80, 60, 0.45)";
      glowColor = "rgba(220, 80, 60, 0.2)";
    } else if (npcOnTile) {
      fillColor = "rgba(255, 200, 80, 0.15)";
      strokeColor = "rgba(255, 200, 80, 0.55)";
      glowColor = "rgba(255, 180, 60, 0.3)";
    } else if (!tile.walkable) {
      fillColor = "rgba(180, 60, 50, 0.10)";
      strokeColor = "rgba(180, 60, 50, 0.30)";
      glowColor = "rgba(180, 60, 50, 0.1)";
    } else {
      fillColor = "rgba(255, 210, 120, 0.14)";
      strokeColor = "rgba(255, 200, 100, 0.40)";
      glowColor = "rgba(255, 180, 60, 0.15)";
    }
    const breathe = Math.sin(timestamp * 0.003) * 0.04 + 1;
    const topRaw    = worldToScreen(hx + 0.5, hy,       elev);
    const rightRaw  = worldToScreen(hx + 1,   hy + 0.5, elev);
    const bottomRaw = worldToScreen(hx + 0.5, hy + 1,   elev);
    const leftRaw   = worldToScreen(hx,       hy + 0.5, elev);
    const top    = applyCamera(topRaw, camera);
    const right  = applyCamera(rightRaw, camera);
    const bottom = applyCamera(bottomRaw, camera);
    const left   = applyCamera(leftRaw, camera);
    ctx.save();
    ctx.globalAlpha = breathe;
    // Soft radial glow underneath for walkable/NPC tiles
    if (tile.walkable || npcOnTile) {
      const cx = (top.x + bottom.x) / 2;
      const cy = (top.y + bottom.y) / 2;
      const rx = Math.abs(right.x - left.x) / 2;
      const ry = Math.abs(bottom.y - top.y) / 2;
      const glowRadius = Math.max(rx, ry) * 1.1;
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, glowRadius);
      grad.addColorStop(0, glowColor);
      grad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      const expand = 2;
      ctx.moveTo(top.x, top.y - expand);
      ctx.lineTo(right.x + expand, right.y);
      ctx.lineTo(bottom.x, bottom.y + expand);
      ctx.lineTo(left.x - expand, left.y);
      ctx.closePath();
      ctx.fill();
    }
    // Diamond fill
    ctx.beginPath();
    ctx.moveTo(top.x, top.y);
    ctx.lineTo(right.x, right.y);
    ctx.lineTo(bottom.x, bottom.y);
    ctx.lineTo(left.x, left.y);
    ctx.closePath();
    ctx.fillStyle = fillColor;
    ctx.fill();
    // Diamond outline
    ctx.beginPath();
    ctx.moveTo(top.x, top.y);
    ctx.lineTo(right.x, right.y);
    ctx.lineTo(bottom.x, bottom.y);
    ctx.lineTo(left.x, left.y);
    ctx.closePath();
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    // Inner highlight line (slightly inset) for depth
    if (tile.walkable && !creatureOnTile) {
      const inset = 0.06;
      const iTop    = applyCamera(worldToScreen(hx + 0.5, hy + inset, elev), camera);
      const iRight  = applyCamera(worldToScreen(hx + 1 - inset, hy + 0.5, elev), camera);
      const iBottom = applyCamera(worldToScreen(hx + 0.5, hy + 1 - inset, elev), camera);
      const iLeft   = applyCamera(worldToScreen(hx + inset, hy + 0.5, elev), camera);
      ctx.beginPath();
      ctx.moveTo(iTop.x, iTop.y);
      ctx.lineTo(iRight.x, iRight.y);
      ctx.lineTo(iBottom.x, iBottom.y);
      ctx.lineTo(iLeft.x, iLeft.y);
      ctx.closePath();
      ctx.strokeStyle = npcOnTile ? "rgba(255, 220, 120, 0.25)" : "rgba(255, 230, 170, 0.18)";
      ctx.lineWidth = 0.75;
      ctx.stroke();
    }
    ctx.restore();
    // Path preview dots from player to hover tile
    this.drawPathPreview(timestamp, state, room, camera);
  }

  // ─── Path Preview ─────────────────────────────────────────────────────────

  private drawPathPreview(
    timestamp: number,
    state: GameState,
    room: import("../engine/state.js").Room,
    camera: Camera,
  ): void {
    const hovered = state.ui.hoveredTile;
    if (!hovered) return;
    const { x: hx, y: hy } = hovered;
    const tile = room.tiles[hy]?.[hx];
    if (!tile || !tile.walkable) { this.cachedPathPreview = null; return; }
    const px = state.player.position.tileX;
    const py = state.player.position.tileY;
    const dist = Math.abs(hx - px) + Math.abs(hy - py);
    if (dist <= 2) { this.cachedPathPreview = null; return; }
    const now = performance.now();
    if (hx !== this.cachedPathHoverTileX || hy !== this.cachedPathHoverTileY || now - this.lastPathComputeTime > 200) {
      this.cachedPathHoverTileX = hx;
      this.cachedPathHoverTileY = hy;
      this.lastPathComputeTime = now;
      const isWalkable = (wx: number, wy: number): boolean => {
        if (wx < 0 || wx >= room.width || wy < 0 || wy >= room.height) return false;
        const t = room.tiles[wy]?.[wx];
        return t ? t.walkable : false;
      };
      this.cachedPathPreview = findPath(isWalkable, { tileX: px, tileY: py }, { tileX: hx, tileY: hy }, { maxLength: 30, allowDiagonal: true });
    }
    const path = this.cachedPathPreview;
    if (!path || path.length < 3) return;
    const { ctx } = this;
    ctx.save();
    for (let i = 1; i < path.length - 1; i++) {
      const step = path[i];
      const stepTile = room.tiles[step.tileY]?.[step.tileX];
      const elev = stepTile ? stepTile.elevation : 0;
      const screen = applyCamera(worldToScreen(step.tileX + 0.5, step.tileY + 0.5, elev), camera);
      const wavePhase = timestamp * 0.004 - i * 0.5;
      const waveBounce = Math.sin(wavePhase) * 0.3 + 0.7;
      ctx.globalAlpha = 0.25 * waveBounce;
      ctx.fillStyle = "#ffd080";
      ctx.beginPath();
      ctx.arc(screen.x, screen.y, 2 * camera.zoom * waveBounce, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  // ─── Click Indicator ──────────────────────────────────────────────────────

  private drawClickIndicator(
    timestamp: number,
    state: GameState,
    room: import("../engine/state.js").Room,
    camera: Camera,
  ): void {
    const indicator = state.ui.clickIndicator;
    if (!indicator) return;
    const elapsed = (timestamp - indicator.time) / 1000;
    if (indicator.reachable) {
      const duration = 0.6;
      if (elapsed > duration) { state.ui.clickIndicator = null; this.clickParticles = []; return; }
      const progress = elapsed / duration;
      const eased = 1 - Math.pow(1 - progress, 3);
      const scale = 1 - eased * 0.5;
      const alpha = (1 - eased) * 0.5;
      const { ctx } = this;
      const { tileX: hx, tileY: hy } = indicator;
      const tile = room.tiles[hy]?.[hx];
      const elev = tile ? tile.elevation : 0;
      const cx = hx + 0.5;
      const cy = hy + 0.5;
      const hs = scale * 0.5;
      const top    = applyCamera(worldToScreen(cx, cy - hs, elev), camera);
      const right  = applyCamera(worldToScreen(cx + hs, cy, elev), camera);
      const bottom = applyCamera(worldToScreen(cx, cy + hs, elev), camera);
      const left   = applyCamera(worldToScreen(cx - hs, cy, elev), camera);
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.moveTo(top.x, top.y); ctx.lineTo(right.x, right.y);
      ctx.lineTo(bottom.x, bottom.y); ctx.lineTo(left.x, left.y);
      ctx.closePath();
      ctx.fillStyle = "rgba(255, 210, 100, 0.4)";
      ctx.fill();
      ctx.shadowColor = "rgba(255, 180, 50, 0.6)";
      ctx.shadowBlur = 8 * (1 - eased);
      ctx.strokeStyle = `rgba(255, 200, 80, ${0.7 * (1 - eased)})`;
      ctx.lineWidth = 2 * (1 - eased * 0.5);
      ctx.stroke();
      ctx.restore();
      // Spawn particles on first frame
      if (this.clickParticles.length === 0) {
        const cScreen = applyCamera(worldToScreen(cx, cy, elev), camera);
        for (let i = 0; i < 6; i++) {
          const angle = (Math.PI * 2 * i) / 6 + Math.random() * 0.5;
          const speed = 15 + Math.random() * 20;
          this.clickParticles.push({
            x: cScreen.x + Math.cos(angle) * 4,
            y: cScreen.y + Math.sin(angle) * 2,
            vx: Math.cos(angle) * speed * 0.3,
            vy: -speed * (0.5 + Math.random() * 0.5),
            life: 1, alpha: 0.6 + Math.random() * 0.3,
            size: 1.5 + Math.random() * 1.5,
          });
        }
      }
      // Update and draw particles
      ctx.save();
      const pdt = 1 / 60;
      for (let i = this.clickParticles.length - 1; i >= 0; i--) {
        const p = this.clickParticles[i];
        p.x += p.vx * pdt; p.y += p.vy * pdt;
        p.vy += 30 * pdt; p.life -= pdt * 2.5;
        if (p.life <= 0) { this.clickParticles.splice(i, 1); continue; }
        ctx.globalAlpha = p.alpha * p.life;
        ctx.fillStyle = "#ffd080";
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * p.life, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    } else {
      // Unreachable: brief red X flash
      const duration = 0.4;
      if (elapsed > duration) { state.ui.clickIndicator = null; return; }
      const progress = elapsed / duration;
      const alpha = (1 - progress) * 0.6;
      const { ctx } = this;
      const { tileX: hx, tileY: hy } = indicator;
      const tile = room.tiles[hy]?.[hx];
      const elev = tile ? tile.elevation : 0;
      const center = applyCamera(worldToScreen(hx + 0.5, hy + 0.5, elev), camera);
      ctx.save();
      ctx.globalAlpha = alpha;
      const xSize = 8 * camera.zoom * (1 - progress * 0.3);
      ctx.strokeStyle = "rgba(220, 70, 50, 0.8)";
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(center.x - xSize, center.y - xSize * 0.5);
      ctx.lineTo(center.x + xSize, center.y + xSize * 0.5);
      ctx.moveTo(center.x + xSize, center.y - xSize * 0.5);
      ctx.lineTo(center.x - xSize, center.y + xSize * 0.5);
      ctx.stroke();
      const top    = applyCamera(worldToScreen(hx + 0.5, hy, elev), camera);
      const right  = applyCamera(worldToScreen(hx + 1, hy + 0.5, elev), camera);
      const bottom = applyCamera(worldToScreen(hx + 0.5, hy + 1, elev), camera);
      const left   = applyCamera(worldToScreen(hx, hy + 0.5, elev), camera);
      ctx.beginPath();
      ctx.moveTo(top.x, top.y); ctx.lineTo(right.x, right.y);
      ctx.lineTo(bottom.x, bottom.y); ctx.lineTo(left.x, left.y);
      ctx.closePath();
      ctx.strokeStyle = `rgba(220, 70, 50, ${alpha * 0.6})`;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();
    }
  }

  // ─── Whisper Overlay ─────────────────────────────────────────────────────

  /** Queue a series of whisper texts to show one after another. */
  queueWhispers(texts: string[]): void {
    this.whisperQueue.push(...texts);
    // If nothing is playing, kick off the first one after a short delay
    if (this.whisperPhase === "idle" && !this.whisperText) {
      this.whisperDelay = 1.5;
    }
  }

  private updateWhisper(dt: number): void {
    const FADE_IN  = 1.5;  // seconds
    const HOLD     = 4.0;
    const FADE_OUT = 2.0;
    const GAP      = 1.5;  // seconds between whispers

    if (this.whisperPhase === "idle") {
      // Waiting for delay before next whisper
      if (this.whisperQueue.length > 0) {
        this.whisperDelay -= dt;
        if (this.whisperDelay <= 0) {
          this.whisperText  = this.whisperQueue.shift()!;
          this.whisperPhase = "fadein";
          this.whisperTimer = 0;
          this.whisperAlpha = 0;
        }
      }
      return;
    }

    this.whisperTimer += dt;

    switch (this.whisperPhase) {
      case "fadein":
        this.whisperAlpha = Math.min(1, this.whisperTimer / FADE_IN);
        if (this.whisperTimer >= FADE_IN) {
          this.whisperPhase = "hold";
          this.whisperTimer = 0;
        }
        break;
      case "hold":
        this.whisperAlpha = 1;
        if (this.whisperTimer >= HOLD) {
          this.whisperPhase = "fadeout";
          this.whisperTimer = 0;
        }
        break;
      case "fadeout":
        this.whisperAlpha = Math.max(0, 1 - this.whisperTimer / FADE_OUT);
        if (this.whisperTimer >= FADE_OUT) {
          this.whisperText  = null;
          this.whisperAlpha = 0;
          this.whisperPhase = "idle";
          this.whisperDelay = GAP;
        }
        break;
    }
  }

  private drawWhisper(): void {
    if (!this.whisperText || this.whisperAlpha <= 0) return;

    const { ctx } = this;
    const W = this.logW;
    const H = this.logH;
    const y = H - 60;

    ctx.save();
    ctx.globalAlpha = this.whisperAlpha * 0.85;

    // Subtle text shadow for readability
    ctx.shadowColor = "rgba(10, 10, 20, 0.7)";
    ctx.shadowBlur  = 8;

    ctx.textAlign    = "center";
    ctx.textBaseline = "middle";
    ctx.font         = "italic 14px Georgia, serif";
    ctx.fillStyle    = "#e8d0b0";
    ctx.fillText(this.whisperText, W / 2, y, W - 60);

    ctx.restore();
  }
}
